import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Rating,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  SentimentVeryDissatisfied as VeryBadIcon,
  SentimentDissatisfied as BadIcon,
  SentimentNeutral as NeutralIcon,
  SentimentSatisfied as GoodIcon,
  SentimentVerySatisfied as ExcellentIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuthStore } from '@store/authStore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { 
  PictureAsPdf as PdfIcon, 
  FileDownload as DownloadIcon,
  SportsTennis as TennisIcon,
  SportsBasketball as BasketballIcon,
  SportsSoccer as FootballIcon,
  SportsVolleyball as VolleyballIcon,
  SportsCricket as CricketIcon,
  SportsHandball as BadmintonIcon,
  Sports as SportsIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

interface BasicPerformanceItem {
  id: string;
  itemCode?: string;
  name: string;
  category: string;
  rating: number;
  notes: string;
  areaOfImprovement?: string[];
  suggestedPractices?: string[];
  areaOfImprovementOptions?: string[];
  suggestedPracticesOptions?: string[];
  evaluationPeriod: string;
  coachId: string;
  playerId: string;
  notApplicable?: boolean; // New field for Not Applicable option
}

interface EvaluationRecord {
  id: string;
  sequenceNumber: number;
  period: string;
  dateRange: { start: string; end: string };
  submittedAt: string;
  averageRating: number;
  notes: string;
  items: BasicPerformanceItem[];
  assessmentType?: string; // Track assessment type to prevent mixing
  coachId?: string | null; // Coach ID who submitted this evaluation
  submitted?: boolean | string | number; // Submitted status from database
}

interface SkillProgressPoint {
  id: string;
  rating: number;
  evaluatedAt: string;
  areaOfImprovement?: string[];
  suggestedPractices?: string[];
}

interface RatingChangeItem {
  itemKey: string;
  itemName: string;
  previous: number;
  current: number;
  direction: 'up' | 'down' | 'same';
}

interface LastSaveSummary {
  increased: number;
  decreased: number;
  same: number;
  daysSincePreviousSave: number | null;
  currentSavedAt: string;
  previousTotalScore: number;
  currentTotalScore: number;
  maxTotalScore: number;
  improvementRatePct: number;
  totalScorePct: number;
}

interface BasicPerformanceTrackingProps {
  player: any;
  coachId?: string;
  selectedCoach?: string; // 'self' for self-evaluation, coach ID for coach reports
  assessmentType?: 'high-level' | 'master-training' | 'fitness' | 'drill'; // Assessment type passed as prop
  trainingLevel?: 'beginner' | 'intermediate' | 'advanced' | 'pro';
  coachOptions?: Array<{ id: string; name: string }>; // Coach options for dropdown
  selectedSport?: string; // Selected sport name
  availableSports?: string[]; // Available sports for selection
  onCoachChange?: (coachId: string) => void; // Callback when coach selection changes
  onSportChange?: (sport: string) => void; // Callback when sport selection changes
  onAddSport?: () => void; // Callback to add new sport
}

const BasicPerformanceTracking: React.FC<BasicPerformanceTrackingProps> = ({
  player,
  coachId,
  selectedCoach,
  assessmentType = 'high-level', // Default to high-level assessment
  trainingLevel,
  coachOptions = [],
  selectedSport = 'Badminton',
  availableSports = ['Badminton'],
  onCoachChange,
  onSportChange,
  onAddSport,
}) => {
  const location = useLocation();
  const { user } = useAuthStore();
  
  // Determine if this is self-evaluation mode
  // If selectedCoach is undefined or empty, default to 'self' for self-evaluation
  const isSelfEvaluation = !selectedCoach || selectedCoach === 'self' || selectedCoach === '';
  const isCoachAggregateEvaluation = selectedCoach === 'coach';
  
  // Determine if current user is a coach
  const isCoach = user?.role === 'coach' || user?.role === 'admin';
  
  // Determine if current user is a player
  const isPlayer = user?.role === 'player';
  
  // Determine if coach can edit (coach is evaluating for a player)
  const canCoachEdit = isCoach && !isSelfEvaluation && coachId && selectedCoach === coachId;
  
  // Determine if player can edit (only for self-evaluation)
  const canPlayerEdit = isPlayer && isSelfEvaluation;

  // Coaches can also complete their own self-evaluations in "My Trainings" (coach-as-player).
  // This enables the same bulk rating + save/submit UX that players have.
  const canCoachSelfEdit = isCoach && isSelfEvaluation;
  
  // Determine if user can edit (coach evaluating OR player self-evaluating)
  const canEdit = canCoachEdit || canPlayerEdit || canCoachSelfEdit;
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(true);
  
  // Safety check for player
  if (!player) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  const [performanceItems, setPerformanceItems] = useState<BasicPerformanceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [evaluationPeriod, setEvaluationPeriod] = useState<string>('quarter-1'); // Default to Q1
  const [evaluationYear, setEvaluationYear] = useState<number>(new Date().getFullYear());
  const [notes, setNotes] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [previousEvaluations, setPreviousEvaluations] = useState<EvaluationRecord[]>([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState<string>('current');
  const [coachEvaluations, setCoachEvaluations] = useState<Record<string, EvaluationRecord[]>>({});
  const [saving, setSaving] = useState(false);
  
  // Reset saving state when component props change (player, sport, assessment type, or coach changes)
  useEffect(() => {
    setSaving(false);
  }, [player?.id, selectedSport, assessmentType, selectedCoach]);
  
  // Debug logging for state
  useEffect(() => {
    console.log('[BasicPerformance] Component state:', {
      selectedCoach,
      isSelfEvaluation,
      selectedEvaluation,
      playerId: player?.id,
      coachId,
      assessmentType
    });
  }, [selectedCoach, isSelfEvaluation, selectedEvaluation, player?.id, coachId, assessmentType]);
  const [currentDateRange, setCurrentDateRange] = useState<{start: string, end: string}>({start: '', end: ''});
  const [activeTab, setActiveTab] = useState<number>(0);
  const [sequenceCounter, setSequenceCounter] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [showAllSkills, setShowAllSkills] = useState<boolean>(false);
  const [coachName, setCoachName] = useState<string>('');
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>({});

  // State for assessment items loaded from database
  const [assessmentItems, setAssessmentItems] = useState<Array<{
    id: string;
    name: string;
    category: string;
    areaOfImprovementOptions?: string[];
    suggestedPracticesOptions?: string[];
  }>>([]);
  const [loadingItems, setLoadingItems] = useState(true);
  
  // Bulk rating state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkRating, setBulkRating] = useState<number>(0);
  const [expandedSkillCard, setExpandedSkillCard] = useState<string | null>(null);
  const [skillProgressByCode, setSkillProgressByCode] = useState<Record<string, SkillProgressPoint[]>>({});
  const [savedRatingsSnapshot, setSavedRatingsSnapshot] = useState<Record<string, number>>({});
  const [savedSnapshotAt, setSavedSnapshotAt] = useState<string | null>(null);
  const [lastSaveSummary, setLastSaveSummary] = useState<LastSaveSummary | null>(null);
  const [lastSaveChangedItems, setLastSaveChangedItems] = useState<RatingChangeItem[]>([]);
  const [isLevelCompleted, setIsLevelCompleted] = useState(false);
  const [lockingLevel, setLockingLevel] = useState(false);
  
  // Filter state for rated/not-rated
  const [filterRatedStatus, setFilterRatedStatus] = useState<string>('all'); // 'all', 'rated', 'not-rated', '1', '2', '3', '4', '5'

  // Load sport-specific assessment items from database
  // CRITICAL: Clear all state when assessmentType changes to prevent stale data
  useEffect(() => {
    // Clear all state immediately when assessmentType changes
    console.log('[Assessment Items] AssessmentType changed to:', assessmentType, '- Clearing all state');
    setAssessmentItems([]);
    setPerformanceItems([]);
    setSelectedEvaluation('current');
    setNotes('');
    setIsSubmitted(false);
    
    const loadAssessmentItems = async () => {
      if (!selectedSport || !assessmentType) {
        console.log('[Assessment Items] Missing required props:', { selectedSport, assessmentType });
        setAssessmentItems([]); // Clear items if props missing
        return;
      }
      
      console.log('[Assessment Items] Loading items for:', { selectedSport, assessmentType });
      setLoadingItems(true);
      try {
        // Add cache-busting parameter to ensure fresh data
        const levelParam = assessmentType === 'master-training' && trainingLevel ? `&level=${encodeURIComponent(trainingLevel)}` : '';
        const url = `/api/performance/assessment-items/${selectedSport}/${assessmentType}?t=${Date.now()}${levelParam}`;
        console.log('[Assessment Items] Fetching from:', url);
        
        const response = await fetch(url, {
          cache: 'no-store', // Prevent browser caching
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        console.log('[Assessment Items] Response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('[Assessment Items] Response data:', result);
        
        if (result.success && result.data && Array.isArray(result.data)) {
          console.log('[Assessment Items] Setting items:', result.data.length, 'items for', assessmentType);
      // Verify the items match the assessmentType (only for high-level and master-training)
      if (assessmentType === 'high-level' || assessmentType === 'master-training') {
        const expectedCount = assessmentType === 'master-training' ? 64 : 7;
            if (result.data.length !== expectedCount) {
              console.warn(`[Assessment Items] Expected ${expectedCount} items for ${assessmentType}, got ${result.data.length}`);
            }
          }
          setAssessmentItems(result.data);
        } else {
          console.warn('[Assessment Items] No items found or invalid response:', result);
          setAssessmentItems([]);
        }
      } catch (error) {
        console.error('[Assessment Items] Error loading assessment items:', error);
        setAssessmentItems([]);
      } finally {
        setLoadingItems(false);
      }
    };
    
    loadAssessmentItems();
  }, [selectedSport, assessmentType, trainingLevel]);
  
  // Reload performance data when assessment items change or period/year/coach changes
  // BUT only if we're not viewing a submitted evaluation
  // CRITICAL: This ensures fresh data is loaded when assessmentType changes
  useEffect(() => {
    if (!loadingItems && player?.id && selectedEvaluation === 'current' && assessmentItems.length > 0) {
      console.log('[BasicPerformance] Loading performance data for', assessmentItems.length, 'items');
      loadPerformanceData();
    }
  }, [assessmentItems, loadingItems, player?.id, selectedEvaluation, evaluationPeriod, evaluationYear, selectedCoach]);

  // Get current assessment items - ONLY use database items, no fallback to Badminton
  // Each sport must have its own entries in the database
  // Use useMemo to ensure predefinedItems updates when assessmentItems changes
  const defaultGuidanceOption = 'Consult the coaches';
  const normalizeGuidanceOptions = (options?: string[]) => {
    if (!Array.isArray(options) || options.length === 0) return [defaultGuidanceOption];
    return options.filter(Boolean);
  };
  const getDisplayItemName = (name: string) => String(name || '').replace(/^\s*\d+\s*[-.)]\s*/, '');

  const predefinedItems = useMemo(() => {
    return assessmentItems.map(item => ({
      itemCode: item.id,
      name: item.name,
      category: item.category,
      areaOfImprovementOptions: normalizeGuidanceOptions(item.areaOfImprovementOptions),
      suggestedPracticesOptions: normalizeGuidanceOptions(item.suggestedPracticesOptions),
    }));
  }, [assessmentItems]);
  
  // Debug logging
  useEffect(() => {
    console.log('[BasicPerformance] Assessment items state:', {
      assessmentItemsCount: assessmentItems.length,
      predefinedItemsCount: predefinedItems.length,
      loadingItems,
      selectedSport,
      assessmentType
    });
  }, [assessmentItems, predefinedItems, loadingItems, selectedSport, assessmentType]);

  const getSmileyIcon = (rating: number) => {
    if (rating === 0) return <Typography variant="body2" color="text.secondary">-</Typography>;
    if (rating <= 1) return <VeryBadIcon sx={{ color: '#f44336' }} />;
    if (rating <= 2) return <BadIcon sx={{ color: '#ff9800' }} />;
    if (rating <= 3) return <NeutralIcon sx={{ color: '#ffc107' }} />;
    if (rating <= 4) return <GoodIcon sx={{ color: '#4caf50' }} />;
    return <ExcellentIcon sx={{ color: '#2196f3' }} />;
  };

  const getPerformanceLevel = (rating: number) => {
    if (rating === 0) return { text: 'Not Rated', color: '#9e9e9e' };
    if (rating <= 1) return { text: 'Needs Improvement', color: '#f44336' };
    if (rating <= 2) return { text: 'Below Average', color: '#ff9800' };
    if (rating <= 3) return { text: 'Average', color: '#ffc107' };
    if (rating <= 4) return { text: 'Good', color: '#4caf50' };
    return { text: 'Excellent', color: '#2196f3' };
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: { bg: string; text: string } } = {
      // Quick Assessment categories
      'Footwork': { bg: '#e8f5e8', text: '#2e7d32' },
      'Grip and Rotation': { bg: '#fff8e1', text: '#f57f17' },
      'Forehand Shots': { bg: '#e3f2fd', text: '#1565c0' },
      'Backhand Shots': { bg: '#e1f5fe', text: '#0277bd' },
      'Fitness': { bg: '#f3e5f5', text: '#7b1fa2' },
      'Follow Instructions': { bg: '#e0f2f1', text: '#00695c' },
      'Interest': { bg: '#fce4ec', text: '#c2185b' },
      
      // Advanced Assessment categories
      'Forehand Strokes': { bg: '#e3f2fd', text: '#1565c0' },
      'Backhand Strokes': { bg: '#e1f5fe', text: '#0277bd' },
      'Behavioural skills': { bg: '#fff3e0', text: '#e65100' },
    };
    return colors[category] || { bg: '#f5f5f5', text: '#666' };
  };

  const getPeriodLabel = (period: string): string => {
    if (period.startsWith('month-')) {
      const monthIndex = parseInt(period.split('-')[1]) - 1;
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return monthNames[monthIndex];
    } else if (period.startsWith('quarter-')) {
      const quarterNum = period.split('-')[1];
      return `Q${quarterNum}`;
    } else if (period === 'yearly') {
      return 'Yearly';
    }
    return period;
  };

  const calculateDateRange = (period: string, year?: number) => {
    const selectedYear = year || evaluationYear;
    
    // Helper function to format date as YYYY-MM-DD without timezone conversion
    const formatDate = (date: Date): string => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    // Handle months (1-12)
    if (period.startsWith('month-')) {
      const monthIndex = parseInt(period.split('-')[1]) - 1; // 0-11
      // Start of month: first day of the selected month (1st)
      const startDate = new Date(selectedYear, monthIndex, 1);
      // End of month: last day of the selected month (day 0 of next month gives last day of current month)
      const endDate = new Date(selectedYear, monthIndex + 1, 0);
      
      return {
        start: formatDate(startDate),
        end: formatDate(endDate)
      };
    }
    // Handle quarters (Q1-Q4)
    else if (period.startsWith('quarter-')) {
      const quarterNum = parseInt(period.split('-')[1]); // 1-4
      const quarterStartMonth = (quarterNum - 1) * 3; // 0, 3, 6, 9
      // Start of quarter: first day of first month
      const start = new Date(selectedYear, quarterStartMonth, 1);
      // End of quarter: last day of last month (day 0 of month after quarter)
      const end = new Date(selectedYear, quarterStartMonth + 3, 0);
      
      return {
        start: formatDate(start),
        end: formatDate(end)
      };
    }
    // Handle yearly
    else if (period === 'yearly') {
      // Start of year: January 1st
      const start = new Date(selectedYear, 0, 1);
      // End of year: December 31st
      const end = new Date(selectedYear, 11, 31);
      
      return {
        start: formatDate(start),
        end: formatDate(end)
      };
    }
    // Default to current month
    else {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const start = new Date(currentYear, currentMonth, 1);
      const end = new Date(currentYear, currentMonth + 1, 0);
      
      return {
        start: formatDate(start),
        end: formatDate(end)
      };
    }
  };

  // Helper functions for robust date range comparison
  const extractYear = (dateStr: string | undefined): number | null => {
    if (!dateStr) return null;
    // Handle both YYYY-MM-DD and other formats
    const yearMatch = dateStr.match(/^(\d{4})/);
    return yearMatch ? parseInt(yearMatch[1], 10) : null;
  };

  const normalizeDate = (dateStr: string | undefined): string | null => {
    if (!dateStr) return null;
    // Extract just the date part (YYYY-MM-DD) if there's a time component
    return dateStr.split('T')[0].split(' ')[0].split('+')[0].split('Z')[0];
  };

  const compareDateRanges = (
    range1: { start?: string; end?: string } | undefined,
    range2: { start: string; end: string }
  ): boolean => {
    if (!range1 || !range1.start || !range1.end) return false;
    
    // Normalize dates for comparison
    const normalized1Start = normalizeDate(range1.start);
    const normalized1End = normalizeDate(range1.end);
    const normalized2Start = normalizeDate(range2.start);
    const normalized2End = normalizeDate(range2.end);
    
    // Compare normalized dates
    return normalized1Start === normalized2Start && normalized1End === normalized2End;
  };

  const loadPreviousEvaluations = async () => {
    try {
      // Load evaluations for current coach/self - filter by sport
      // Use selectedCoach if available, otherwise use coachId
      // For coach login: use the actual coachId, not 'self'
      const coachIdParam = isSelfEvaluation || isCoachAggregateEvaluation ? '' : (selectedCoach || coachId || '');
      const apiUrl = `/api/performance/basic/player/${player.id}?sport=${selectedSport || 'Badminton'}${coachIdParam ? `&coachId=${coachIdParam}` : ''}`;
      console.log('[BasicPerformance] Loading evaluations with:', { 
        coachIdParam, 
        selectedCoach, 
        coachId, 
        isSelfEvaluation,
        apiUrl 
      });
      const response = await fetch(apiUrl);
      const result = await response.json();
      
      if (result.success) {
        // Filter evaluations by assessmentType to prevent mixing high-level and low-level
        // Handle both old ('quick'/'advanced') and new ('high-level'/'low-level') values
        const filteredData = result.data.filter((evaluation: any) => {
          const evalAssessmentType = evaluation.assessmentType || 'high-level';
          // Map old values to new values for comparison
          const normalizedEvalType = evalAssessmentType === 'quick' ? 'high-level' : 
                                     (evalAssessmentType === 'advanced' || evalAssessmentType === 'low-level') ? 'master-training' : 
                                     evalAssessmentType;
          return normalizedEvalType === assessmentType;
        });
        
        console.log(`[BasicPerformance] Filtered ${filteredData.length} evaluations for assessmentType: ${assessmentType} (from ${result.data.length} total)`);
        
        const evaluations: EvaluationRecord[] = filteredData.map((evaluation: any, index: number) => {
          const evalRecord = {
            id: evaluation.id,
            sequenceNumber: evaluation.sequenceNumber || index + 1,
            period: evaluation.period,
            dateRange: evaluation.dateRange,
            submittedAt: evaluation.submittedAt,
            averageRating: evaluation.averageRating,
            notes: evaluation.notes,
            items: evaluation.items || [],
            coachId: evaluation.coachId,
            assessmentType: evaluation.assessmentType || assessmentType,
            submitted: evaluation.submitted || false // Include submitted status from API
          };
          
          // Debug log to verify items are present
          if (evalRecord.items && evalRecord.items.length > 0) {
            console.log(`[BasicPerformance] Loaded evaluation ${evalRecord.id}:`, {
              itemsCount: evalRecord.items.length,
              firstItem: evalRecord.items[0],
              period: evalRecord.period,
              assessmentType: evalRecord.assessmentType
            });
          } else {
            console.warn(`[BasicPerformance] Evaluation ${evalRecord.id} has no items!`);
          }
          
          return evalRecord;
        });
        
        console.log('[BasicPerformance] Loaded', evaluations.length, 'previous evaluations');
        setPreviousEvaluations(evaluations);
        setSequenceCounter(evaluations.length + 1);
        
        // Don't check for submitted here - let loadPerformanceData handle it
        // This matches RankingEvaluationWithTabs where loadExistingRatings sets isSubmitted
      } else {
        console.error('Error loading previous evaluations:', result.message);
        setPreviousEvaluations([]);
        setSequenceCounter(1);
      }
      
      // Load evaluations for each coach to check their status - filter by sport and assessmentType
      // Also ensure current coach's evaluations are loaded even if not in coachOptions
      const coachEvals: Record<string, EvaluationRecord[]> = {};
      const coachesToLoad = [...coachOptions];
      
      // If current coach is not in coachOptions, add them to the list
      if (selectedCoach && selectedCoach !== 'self' && selectedCoach !== 'coach' && !coachOptions.find(c => c.id === selectedCoach)) {
        console.log('[BasicPerformance] Current coach not in coachOptions, adding to load list:', selectedCoach);
        coachesToLoad.push({ id: selectedCoach, name: 'Current Coach' });
      }
      
      if (coachesToLoad.length > 0) {
        for (const coach of coachesToLoad) {
          try {
            const coachApiUrl = `/api/performance/basic/player/${player.id}?coachId=${coach.id}&sport=${selectedSport || 'Badminton'}`;
            console.log('[BasicPerformance] Loading evaluations for coach:', { coachId: coach.id, coachName: coach.name, apiUrl: coachApiUrl });
            const coachResponse = await fetch(coachApiUrl);
            const coachResult = await coachResponse.json();
            console.log('[BasicPerformance] Coach evaluations API response:', { 
              coachId: coach.id, 
              success: coachResult.success, 
              dataCount: coachResult.data?.length || 0,
              rawData: coachResult.data 
            });
            if (coachResult.success) {
              // Filter by assessmentType to prevent mixing high-level and low-level
              // Handle both old ('quick'/'advanced') and new ('high-level'/'low-level') values
              const filteredCoachData = coachResult.data.filter((evaluation: any) => {
                const evalAssessmentType = evaluation.assessmentType || 'high-level';
                const normalizedEvalType = evalAssessmentType === 'quick' ? 'high-level' : 
                                           (evalAssessmentType === 'advanced' || evalAssessmentType === 'low-level') ? 'master-training' : 
                                           evalAssessmentType;
                return normalizedEvalType === assessmentType;
              });
              
              console.log('[BasicPerformance] Filtered coach evaluations:', { 
                coachId: coach.id, 
                beforeFilter: coachResult.data.length, 
                afterFilter: filteredCoachData.length 
              });
              
              coachEvals[coach.id] = filteredCoachData.map((evaluation: any) => ({
                id: evaluation.id,
                sequenceNumber: evaluation.sequenceNumber || 1,
                period: evaluation.period,
                dateRange: evaluation.dateRange,
                submittedAt: evaluation.submittedAt,
                averageRating: evaluation.averageRating,
                notes: evaluation.notes,
                items: evaluation.items || [],
                coachId: evaluation.coachId,
                assessmentType: evaluation.assessmentType || assessmentType,
                submitted: evaluation.submitted || false // Include submitted status from API
              }));
            }
          } catch (err) {
            console.error(`Error loading evaluations for coach ${coach.id}:`, err);
          }
        }
        // Also load self-evaluations - filter by sport and assessmentType
        try {
          const selfResponse = await fetch(`/api/performance/basic/player/${player.id}?coachId=&sport=${selectedSport || 'Badminton'}`);
          const selfResult = await selfResponse.json();
          if (selfResult.success) {
            // Filter by assessmentType
            // Handle both old ('quick'/'advanced') and new ('high-level'/'low-level') values
            const filteredSelfData = selfResult.data.filter((evaluation: any) => {
              const evalAssessmentType = evaluation.assessmentType || 'high-level';
              const normalizedEvalType = evalAssessmentType === 'quick' ? 'high-level' : 
                                         (evalAssessmentType === 'advanced' || evalAssessmentType === 'low-level') ? 'master-training' : 
                                         evalAssessmentType;
              return normalizedEvalType === assessmentType && !evaluation.coachId;
            });
            
            coachEvals['self'] = filteredSelfData.map((evaluation: any) => ({
              id: evaluation.id,
              sequenceNumber: evaluation.sequenceNumber || 1,
              period: evaluation.period,
              dateRange: evaluation.dateRange,
              submittedAt: evaluation.submittedAt,
              averageRating: evaluation.averageRating,
              notes: evaluation.notes,
              items: evaluation.items || [],
              coachId: null,
              assessmentType: evaluation.assessmentType || assessmentType,
              submitted: evaluation.submitted || false // Include submitted status from API
            }));
          }
        } catch (err) {
          console.error('Error loading self-evaluations:', err);
        }
        console.log('[BasicPerformance] Final coachEvaluations:', Object.keys(coachEvals).map(key => ({
          coachId: key,
          count: coachEvals[key].length,
          submittedCount: coachEvals[key].filter(e => e.submitted).length
        })));
        setCoachEvaluations(coachEvals);
      } else {
        console.log('[BasicPerformance] No coachOptions, skipping coach evaluations load');
      }
    } catch (error) {
      console.error('Error loading previous evaluations:', error);
      setPreviousEvaluations([]);
      setSequenceCounter(1);
    }
  };

  const loadPerformanceData = async () => {
    setLoading(true);
    try {
      if (predefinedItems.length === 0) {
        setPerformanceItems([]);
        setIsSubmitted(false);
        setLoading(false);
        return;
      }

      // SIMPLE LOGIC: Check table for player/coach/period match
      // 1. Query API
      // 2. Find exact match (player/coach/period/dateRange/assessmentType)
      // 3. Check submitted field
      // 4. If submitted=true: disable buttons, load submitted data
      // 5. If submitted=false: enable buttons, load saved data
      // 6. If no match: enable buttons, show empty form
      
      const isContinuousMode = assessmentType === 'master-training';
      const todayDate = new Date().toISOString().split('T')[0];
      const currentDateRange = isContinuousMode
        ? { start: todayDate, end: todayDate }
        : calculateDateRange(evaluationPeriod, evaluationYear);
      const coachIdParam = isSelfEvaluation || isCoachAggregateEvaluation ? '' : (selectedCoach || coachId || '');
      const apiUrl = `/api/performance/basic/player/${player.id}?coachId=${coachIdParam}&sport=${selectedSport || 'Badminton'}`;
      
      console.log('[BasicPerformance] Checking table for:', {
        playerId: player.id,
        coachId: coachIdParam || '(self)',
        period: evaluationPeriod,
        year: evaluationYear,
        dateRange: currentDateRange,
        assessmentType
      });

      const response = await fetch(apiUrl);
      const result = await response.json();

      // Helper to normalize dates
      const normalizeDate = (dateStr: string | undefined) => {
        if (!dateStr) return '';
        return dateStr.split('T')[0];
      };
      const currentStart = normalizeDate(currentDateRange.start);
      const currentEnd = normalizeDate(currentDateRange.end);

      // Find EXACT match for current period/player/coach/assessmentType
      let matchedEvaluation: any = null;

      console.log('[BasicPerformance] API returned', result.data?.length || 0, 'evaluations');
      
      if (result.success && result.data && result.data.length > 0) {
        console.log('[BasicPerformance] Checking evaluations for match:', {
          lookingFor: {
            period: evaluationPeriod,
            dateRange: { start: currentStart, end: currentEnd },
            coachId: coachIdParam || '(self)',
            assessmentType
          }
        });

        for (const evaluation of result.data) {
          console.log('[BasicPerformance] Checking evaluation:', {
            id: evaluation.id,
            period: evaluation.period,
            dateRange: evaluation.dateRange,
            coachId: evaluation.coachId || '(self)',
            assessmentType: evaluation.assessmentType,
            submitted: evaluation.submitted,
            submittedType: typeof evaluation.submitted
          });

          // Normalize assessment type
          const evalAssessmentType = evaluation.assessmentType || 'high-level';
          const normalizedEvalType = evalAssessmentType === 'quick' ? 'high-level' : 
                                     (evalAssessmentType === 'advanced' || evalAssessmentType === 'low-level') ? 'master-training' : 
                                     evalAssessmentType;
          if (normalizedEvalType !== assessmentType) {
            console.log('[BasicPerformance] Assessment type mismatch:', normalizedEvalType, 'vs', assessmentType);
            continue;
          }

          if (!isContinuousMode) {
          // Match period
          if (evaluation.period !== evaluationPeriod) {
            console.log('[BasicPerformance] Period mismatch:', evaluation.period, 'vs', evaluationPeriod);
            continue;
          }

          // Match dateRange
          const evalStart = normalizeDate(evaluation.dateRange?.start);
          const evalEnd = normalizeDate(evaluation.dateRange?.end);
          if (evalStart !== currentStart || evalEnd !== currentEnd) {
            console.log('[BasicPerformance] DateRange mismatch:', { evalStart, evalEnd }, 'vs', { currentStart, currentEnd });
            continue;
            }
          }

          // Match coachId
          const coachMatch = isCoachAggregateEvaluation
            ? Boolean(evaluation.coachId)
            : ((!coachIdParam && !evaluation.coachId) || (coachIdParam && evaluation.coachId === coachIdParam));
          if (!coachMatch) {
            console.log('[BasicPerformance] CoachId mismatch:', evaluation.coachId || '(self)', 'vs', coachIdParam || '(self)');
            continue;
          }

          // Found match (exact for period mode, latest-coach/type for continuous mode)
          console.log('[BasicPerformance] ✓ EXACT MATCH FOUND:', {
            id: evaluation.id,
            submitted: evaluation.submitted,
            submittedType: typeof evaluation.submitted
          });
          matchedEvaluation = evaluation;
          break; // result.data already sorted by created_at DESC
        }
      }

      // Check submitted status
      const isSubmitted = matchedEvaluation && (
        matchedEvaluation.submitted === true || 
        matchedEvaluation.submitted === 'true' || 
        matchedEvaluation.submitted === 1
      );

      console.log('[BasicPerformance] FINAL RESULT:', {
        found: !!matchedEvaluation,
        matchedEvaluationId: matchedEvaluation?.id,
        matchedSubmitted: matchedEvaluation?.submitted,
        matchedSubmittedType: typeof matchedEvaluation?.submitted,
        isSubmitted: isSubmitted,
        willSetIsSubmitted: isSubmitted
      });

      // Set isSubmitted state - this controls button enable/disable
      console.log('[BasicPerformance] Setting isSubmitted to:', isContinuousMode ? false : isSubmitted);
      setIsSubmitted(isContinuousMode ? false : isSubmitted);

      // Load data based on status
      if (matchedEvaluation && matchedEvaluation.items && matchedEvaluation.items.length > 0) {
        // Load the evaluation data (whether submitted or saved)
        console.log('[BasicPerformance] Loading evaluation data, submitted:', isSubmitted);
        if (isCoachAggregateEvaluation) {
          const matchedCoach = matchedEvaluation.coachId
            ? coachOptions.find((c) => c.id === matchedEvaluation.coachId)
            : undefined;
          if (matchedCoach?.name) {
            setCoachName(matchedCoach.name);
          } else if (matchedEvaluation.coachId) {
            setCoachName(`Coach ${String(matchedEvaluation.coachId).slice(0, 8)}`);
          } else {
            setCoachName('Coach Evaluation');
          }
        }
        const savedItemsMap = new Map(matchedEvaluation.items.map((item: any) => [item.name, item]));
        
        const loadedItems: BasicPerformanceItem[] = predefinedItems.map((item, index) => {
          const savedItem = savedItemsMap.get(item.name) as any;
          return {
            id: `skill-${index}`,
            itemCode: item.itemCode || `skill-${index}`,
            name: item.name,
            category: item.category,
            rating: savedItem?.rating || 0,
            notes: savedItem?.notes || '',
            areaOfImprovement: Array.isArray(savedItem?.areaOfImprovement) && savedItem.areaOfImprovement.length > 0
              ? savedItem.areaOfImprovement
              : [defaultGuidanceOption],
            suggestedPractices: Array.isArray(savedItem?.suggestedPractices) && savedItem.suggestedPractices.length > 0
              ? savedItem.suggestedPractices
              : [defaultGuidanceOption],
            areaOfImprovementOptions: normalizeGuidanceOptions(item.areaOfImprovementOptions),
            suggestedPracticesOptions: normalizeGuidanceOptions(item.suggestedPracticesOptions),
            evaluationPeriod: evaluationPeriod,
            coachId: coachId || '',
            playerId: player.id,
            notApplicable: savedItem?.notApplicable || false,
          };
        });
        
        setPerformanceItems(loadedItems);
        setSavedRatingsSnapshot(buildRatingsSnapshot(loadedItems));
        setSavedSnapshotAt(matchedEvaluation.submittedAt || new Date().toISOString());
        setNotes(matchedEvaluation.notes || '');
      } else {
        // No data found - show empty form
        console.log('[BasicPerformance] No evaluation found, showing empty form');
        const emptyItems: BasicPerformanceItem[] = predefinedItems.map((item, index) => ({
          id: `skill-${index}`,
          itemCode: item.itemCode || `skill-${index}`,
          name: item.name,
          category: item.category,
          rating: 0,
          notes: '',
          areaOfImprovement: [defaultGuidanceOption],
          suggestedPractices: [defaultGuidanceOption],
          areaOfImprovementOptions: normalizeGuidanceOptions(item.areaOfImprovementOptions),
          suggestedPracticesOptions: normalizeGuidanceOptions(item.suggestedPracticesOptions),
          evaluationPeriod: evaluationPeriod,
          coachId: coachId || '',
          playerId: player.id,
          notApplicable: false,
        }));
        setPerformanceItems(emptyItems);
        setSavedRatingsSnapshot(buildRatingsSnapshot(emptyItems));
        setSavedSnapshotAt(null);
        setNotes('');
      }
    } catch (error) {
      console.error('[BasicPerformance] Error loading performance data:', error);
      toast.error('Failed to load performance data');
      setPerformanceItems([]);
      setSavedRatingsSnapshot({});
      setSavedSnapshotAt(null);
      setIsSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  // Load coach name - use selectedCoach or coachId, and check coachOptions first
  const loadCoachName = async () => {
    if (selectedCoach === 'coach') {
      setCoachName('Coach Evaluation');
      return;
    }
    const coachIdToUse = selectedCoach && selectedCoach !== 'self' ? selectedCoach : (coachId && coachId !== 'self' ? coachId : null);
    
    if (!coachIdToUse || selectedCoach === 'self') {
      setCoachName('Self Evaluation');
      return;
    }
    
    // First, check if coach is in coachOptions (faster and already has name)
    const coachFromOptions = coachOptions.find((c: any) => c.id === coachIdToUse);
    if (coachFromOptions && coachFromOptions.name) {
      console.log('[BasicPerformance] Using coach name from coachOptions:', coachFromOptions.name);
      setCoachName(coachFromOptions.name);
      return;
    }
    
    // If not in coachOptions, fetch from API
    try {
      const response = await fetch(`/api/coaches`);
      const data = await response.json();
      if (data.success && data.data) {
        const coach = data.data.find((c: any) => c.id === coachIdToUse);
        if (coach) {
          // Backend API structure: user: { firstName: row.first_name, lastName: row.last_name }
          // So names are in user.firstName and user.lastName (camelCase)
          const firstName = coach.user?.firstName || '';
          const lastName = coach.user?.lastName || '';
          const name = `${firstName} ${lastName}`.trim() || coach.coachIdCode || coach.coach_id_code || 'Unknown Coach';
          console.log('[BasicPerformance] Loaded coach name from API:', name, 'for coachId:', coachIdToUse, 'coach object:', { 
            user_firstName: coach.user?.firstName,
            user_lastName: coach.user?.lastName,
            coachIdCode: coach.coachIdCode,
            coach_id_code: coach.coach_id_code,
            fullCoach: coach
          });
          setCoachName(name);
        } else {
          console.warn('[BasicPerformance] Coach not found in API:', coachIdToUse);
          setCoachName('Unknown Coach');
        }
      } else {
        setCoachName('Unknown Coach');
      }
    } catch (error) {
      console.error('Error loading coach name:', error);
      setCoachName('Unknown Coach');
    }
  };

  const getItemKey = (item: BasicPerformanceItem) => item.itemCode || item.id || item.name;

  const buildRatingsSnapshot = (items: BasicPerformanceItem[]) => {
    const snapshot: Record<string, number> = {};
    items.forEach((item) => {
      snapshot[getItemKey(item)] = Number(item.rating || 0);
    });
    return snapshot;
  };

  const getRatingDeltaSummary = (items: BasicPerformanceItem[]) => {
    const currentSnapshot = buildRatingsSnapshot(items);
    const keys = new Set<string>([
      ...Object.keys(savedRatingsSnapshot || {}),
      ...Object.keys(currentSnapshot || {}),
    ]);
    let increased = 0;
    let decreased = 0;
    let same = 0;
    keys.forEach((key) => {
      const previous = Number(savedRatingsSnapshot?.[key] || 0);
      const current = Number(currentSnapshot?.[key] || 0);
      if (previous <= 0 && current <= 0) return;
      if (current > previous) increased++;
      else if (current < previous) decreased++;
      else same++;
    });
    return { increased, decreased, same, currentSnapshot };
  };

  const getRatingChangeItems = (items: BasicPerformanceItem[]): RatingChangeItem[] => {
    return items
      .map((item) => {
        const itemKey = getItemKey(item);
        const previous = Number(savedRatingsSnapshot?.[itemKey] || 0);
        const current = Number(item.rating || 0);
        let direction: RatingChangeItem['direction'] = 'same';
        if (current > previous) direction = 'up';
        else if (current < previous) direction = 'down';
        return {
          itemKey,
          itemName: item.name,
          previous,
          current,
          direction,
        };
      })
      .filter((row) => row.direction !== 'same');
  };

  const getTrendDirectionForItem = (item: BasicPerformanceItem) => {
    const key = getItemKey(item);
    const previous = Number(savedRatingsSnapshot?.[key] || 0);
    const current = Number(item.rating || 0);
    if (previous <= 0 && current <= 0) return 'same' as const;
    if (current > previous) return 'up' as const;
    if (current < previous) return 'down' as const;
    return 'same' as const;
  };

  // Read URL parameters for period and year on mount and when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const periodParam = urlParams.get('period');
    const yearParam = urlParams.get('year');
    
    if (periodParam && periodParam !== evaluationPeriod) {
      setEvaluationPeriod(periodParam);
    }
    if (yearParam) {
      const year = parseInt(yearParam);
      if (!isNaN(year) && year !== evaluationYear) {
        setEvaluationYear(year);
      }
    }
  }, [location.search, evaluationPeriod, evaluationYear]);

  useEffect(() => {
    if (player?.id) {
      // Don't load performance data here - let the assessmentItems useEffect handle it
      loadPreviousEvaluations();
      setCurrentDateRange(calculateDateRange(evaluationPeriod, evaluationYear));
      loadCoachName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.id, selectedCoach]);
  
  // Separate effect to reload coach name when coachOptions change (but only if coachOptions has valid names)
  // Use a ref to track if we've already loaded to prevent infinite loops
  const coachOptionsLoadedRef = useRef<string>('');
  useEffect(() => {
    if (player?.id && coachOptions.length > 0) {
      // Create a stable key from coachOptions to detect actual changes
      const coachOptionsKey = coachOptions.map(c => `${c.id}:${c.name}`).join(',');
      if (coachOptionsKey !== coachOptionsLoadedRef.current) {
        coachOptionsLoadedRef.current = coachOptionsKey;
        loadCoachName();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachOptions.length]); // Only depend on length to avoid infinite loops
  
  // Clear bulk selection when period, year, evaluation, assessment type, or coach changes
  useEffect(() => {
    setSelectedItems(new Set());
    setBulkRating(0);
  }, [evaluationPeriod, evaluationYear, selectedEvaluation, assessmentType, selectedCoach]);

  useEffect(() => {
    void loadLevelCompletion();
  }, [assessmentType, canCoachEdit, trainingLevel, player?.id, selectedCoach, selectedSport]);

  useEffect(() => {
    setSkillProgressByCode({});
    setSavedSnapshotAt(null);
    setLastSaveSummary(null);
    setLastSaveChangedItems([]);
  }, [player?.id, selectedSport, assessmentType, trainingLevel, selectedCoach]);

  useEffect(() => {
    if (!player?.id || performanceItems.length === 0) return;
    const uniqueCodes = Array.from(new Set(performanceItems.map((item) => item.itemCode || item.id).filter(Boolean)));
    uniqueCodes.forEach((code) => {
      if (!skillProgressByCode[code]) {
        void loadSkillProgress(code);
      }
    });
  }, [player?.id, performanceItems, selectedSport, assessmentType, trainingLevel]);
  
  useEffect(() => {
    // Listen for manual form download events
    // Handle both old ('quick'/'advanced') and new ('high-level'/'low-level') event types
    const handleDownloadForm = (event: CustomEvent) => {
      const eventType = event.detail?.type;
      // Map old types to new types
      const normalizedEventType = eventType === 'quick' ? 'high-level' : 
                                 (eventType === 'advanced' || eventType === 'low-level') ? 'master-training' : 
                                 eventType;
      
      // Check if the event type matches the current assessment type
      if (normalizedEventType === assessmentType) {
        generateManualAssessmentForm();
      }
    };
    
    window.addEventListener('downloadManualForm', handleDownloadForm as EventListener);
    return () => {
      window.removeEventListener('downloadManualForm', handleDownloadForm as EventListener);
    };
  }, [player?.id, evaluationPeriod, evaluationYear, assessmentType, coachId, selectedCoach]);

  useEffect(() => {
    if (selectedEvaluation !== 'current') {
      const selectedEvalData = previousEvaluations.find(evaluation => evaluation.id === selectedEvaluation);
      if (selectedEvalData) {
        // CRITICAL: Verify assessmentType matches - prevent mixing high-level and low-level
        // Handle both old ('quick'/'advanced') and new ('high-level'/'low-level') values
        const evalAssessmentType = selectedEvalData.assessmentType || 'high-level';
        const normalizedEvalType = evalAssessmentType === 'quick' ? 'high-level' : 
                                   (evalAssessmentType === 'advanced' || evalAssessmentType === 'low-level') ? 'master-training' : 
                                   evalAssessmentType;
        if (normalizedEvalType !== assessmentType) {
          console.error(`[BasicPerformance] AssessmentType mismatch! Selected eval is ${evalAssessmentType} (normalized: ${normalizedEvalType}) but current is ${assessmentType}. Resetting to current.`);
          setSelectedEvaluation('current');
          loadPerformanceData();
          return;
        }
        
        console.log('[BasicPerformance] Loading submitted evaluation:', {
          id: selectedEvalData.id,
          itemsCount: selectedEvalData.items?.length || 0,
          period: selectedEvalData.period,
          assessmentType: evalAssessmentType,
          hasNotes: !!selectedEvalData.notes
        });
        
        // Ensure items array exists and has data
        if (selectedEvalData.items && selectedEvalData.items.length > 0) {
          // Group by category for debugging
          const byCategory: Record<string, number> = {};
          selectedEvalData.items.forEach((item: BasicPerformanceItem) => {
            const cat = item.category || 'Unknown';
            byCategory[cat] = (byCategory[cat] || 0) + 1;
          });
          
          console.log('[BasicPerformance] Setting performance items from submitted evaluation:', selectedEvalData.items.length, 'items');
          console.log('[BasicPerformance] Items by category:', byCategory);
          console.log('[BasicPerformance] Sample items (first 5):', selectedEvalData.items.slice(0, 5).map((i: BasicPerformanceItem) => `[${i.category}] ${i.name}`));
          
          const itemMetaByName = new Map(predefinedItems.map((item) => [item.name, item]));
          const normalizedItems = selectedEvalData.items.map((item: BasicPerformanceItem, idx: number) => {
            const meta = itemMetaByName.get(item.name);
            return {
              ...item,
              id: item.id || `skill-selected-${idx}`,
              areaOfImprovement: Array.isArray(item.areaOfImprovement) && item.areaOfImprovement.length > 0
                ? item.areaOfImprovement
                : [defaultGuidanceOption],
              suggestedPractices: Array.isArray(item.suggestedPractices) && item.suggestedPractices.length > 0
                ? item.suggestedPractices
                : [defaultGuidanceOption],
              areaOfImprovementOptions: normalizeGuidanceOptions(meta?.areaOfImprovementOptions),
              suggestedPracticesOptions: normalizeGuidanceOptions(meta?.suggestedPracticesOptions),
            };
          });
          setPerformanceItems(normalizedItems);
          setSavedRatingsSnapshot(buildRatingsSnapshot(normalizedItems));
        } else {
          console.warn('[BasicPerformance] Submitted evaluation has no items!', selectedEvalData);
          setPerformanceItems([]);
          setSavedRatingsSnapshot({});
        }
        
        setNotes(selectedEvalData.notes || '');
        // Use submitted status from evaluation data, default to true for old records without submitted field
        setIsSubmitted(selectedEvalData.submitted !== undefined ? selectedEvalData.submitted : true);
        setCurrentDateRange(selectedEvalData.dateRange);
        // Extract period and year from the evaluation data
        if (selectedEvalData.period) {
          setEvaluationPeriod(selectedEvalData.period);
        }
        if (selectedEvalData.dateRange?.start) {
          const year = new Date(selectedEvalData.dateRange.start).getFullYear();
          setEvaluationYear(year);
        }
      } else {
        console.warn('[BasicPerformance] Selected evaluation not found in previousEvaluations');
      }
    } else {
      console.log('[BasicPerformance] Loading current evaluation - will check for submitted in loadPerformanceData');
      // Don't reset isSubmitted here - let loadPerformanceData check and set it correctly
      // This matches RankingEvaluationWithTabs pattern where loadExistingRatings sets isSubmitted
      loadPerformanceData();
      setCurrentDateRange(calculateDateRange(evaluationPeriod, evaluationYear));
    }
  }, [selectedEvaluation, previousEvaluations, evaluationPeriod, evaluationYear]);

  const handleRatingChange = (skillId: string, newRating: number) => {
    setPerformanceItems(prev => 
      prev.map(item => (item.id === skillId ? { ...item, rating: newRating } : item))
    );
  };

  const handleNotApplicableChange = (skillId: string, notApplicable: boolean) => {
    setPerformanceItems(prev => 
      prev.map(item => 
        item.id === skillId ? { 
          ...item, 
          notApplicable: notApplicable,
          rating: 0
        } : item
      )
    );
    // Remove from selection if marked as not applicable
    if (notApplicable) {
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(skillId);
        return newSet;
      });
    }
  };

  const handleAreaOfImprovementChange = (skillId: string, selectedValues: string[]) => {
    const nextValues = selectedValues.length > 0 ? selectedValues : [defaultGuidanceOption];
    setPerformanceItems(prev =>
      prev.map(item => (item.id === skillId ? { ...item, areaOfImprovement: nextValues } : item))
    );
  };

  const handleSuggestedPracticesChange = (skillId: string, selectedValues: string[]) => {
    const nextValues = selectedValues.length > 0 ? selectedValues : [defaultGuidanceOption];
    setPerformanceItems(prev =>
      prev.map(item => (item.id === skillId ? { ...item, suggestedPractices: nextValues } : item))
    );
  };

  const persistSkillProgressEvent = async (item: BasicPerformanceItem, ratingValue: number) => {
    try {
      const itemCode = item.itemCode || item.id;
      const nowIso = new Date().toISOString();
      // Optimistic append so the trend line updates immediately after save/submit.
      setSkillProgressByCode((prev) => {
        const existing = prev[itemCode] || [];
        return {
          ...prev,
          [itemCode]: [
            ...existing,
            {
              id: `local-${itemCode}-${Date.now()}`,
              rating: Number(ratingValue || 0),
              evaluatedAt: nowIso,
              areaOfImprovement: item.areaOfImprovement && item.areaOfImprovement.length > 0 ? item.areaOfImprovement : [defaultGuidanceOption],
              suggestedPractices: item.suggestedPractices && item.suggestedPractices.length > 0 ? item.suggestedPractices : [defaultGuidanceOption],
            },
          ].slice(-50),
        };
      });

      await fetch('/api/training-scores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId: player.id,
          coachId: selectedCoach === 'self' || selectedCoach === 'coach' ? null : selectedCoach || null,
          sport: selectedSport || 'Badminton',
          itemType: 'skill',
          itemCode,
          itemName: item.name,
          rating: ratingValue,
          periodType: 'yearly',
          source: selectedCoach === 'self' || selectedCoach === 'coach' ? 'self' : 'coach',
          notes: JSON.stringify({
            assessmentType,
            trainingLevel: trainingLevel || null,
            areaOfImprovement: item.areaOfImprovement && item.areaOfImprovement.length > 0 ? item.areaOfImprovement : [defaultGuidanceOption],
            suggestedPractices: item.suggestedPractices && item.suggestedPractices.length > 0 ? item.suggestedPractices : [defaultGuidanceOption],
          }),
          assessmentType,
          trainingLevel: trainingLevel || null,
          areaOfImprovement: item.areaOfImprovement && item.areaOfImprovement.length > 0 ? item.areaOfImprovement : [defaultGuidanceOption],
          suggestedPractices: item.suggestedPractices && item.suggestedPractices.length > 0 ? item.suggestedPractices : [defaultGuidanceOption],
        }),
      });
      if (skillProgressByCode[itemCode]) {
        void loadSkillProgress(itemCode);
      }
    } catch (error) {
      console.error('Failed to persist skill progress event:', error);
    }
  };

  const persistSkillProgressSnapshot = async (items: BasicPerformanceItem[]) => {
    if (!player?.id || !Array.isArray(items) || items.length === 0) return;
    const persistedItems = items.filter((item) => Number(item.rating || 0) >= 0);
    await Promise.allSettled(
      persistedItems.map((item) => persistSkillProgressEvent(item, Number(item.rating || 0)))
    );
  };

  const loadSkillProgress = async (itemCode: string) => {
    if (!player?.id || !itemCode) return;
    try {
      const params = new URLSearchParams({
        sport: selectedSport || 'Badminton',
        itemType: 'skill',
        playerId: player.id,
        itemCode,
        assessmentType: assessmentType || 'master-training',
        trainingLevel: trainingLevel || '',
        limit: '50',
      });
      const response = await fetch(`/api/training-scores?${params.toString()}`);
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        const points: SkillProgressPoint[] = [...result.data]
          .reverse()
          .map((row: any) => {
            let parsedNotes: any = {};
            try {
              parsedNotes = row.notes ? JSON.parse(row.notes) : {};
            } catch {
              parsedNotes = {};
            }
            return {
              id: row.id,
              rating: Number(row.rating || 0),
              evaluatedAt: row.evaluatedAt,
              areaOfImprovement: row.areaOfImprovement || parsedNotes.areaOfImprovement || [defaultGuidanceOption],
              suggestedPractices: row.suggestedPractices || parsedNotes.suggestedPractices || [defaultGuidanceOption],
            };
          });
        setSkillProgressByCode((prev) => ({ ...prev, [itemCode]: points }));
      }
    } catch (error) {
      console.error('Failed to load skill progress:', error);
    }
  };

  const loadLevelCompletion = async () => {
    if (assessmentType !== 'master-training' || !canCoachEdit || !trainingLevel || !player?.id || !selectedCoach) {
      setIsLevelCompleted(false);
      return;
    }
    try {
      const params = new URLSearchParams({
        playerId: player.id,
        coachId: selectedCoach,
        sport: selectedSport || 'Badminton',
        trainingLevel,
      });
      const response = await fetch(`/api/training-level-completion?${params.toString()}`);
      const result = await response.json();
      setIsLevelCompleted(Boolean(result?.success && result?.data?.completed));
    } catch (error) {
      console.error('Failed to load level completion status:', error);
      setIsLevelCompleted(false);
    }
  };

  const handleMarkLevelComplete = async () => {
    if (!player?.id || !selectedCoach || !trainingLevel) return;
    const confirmed = window.confirm('Mark this level as complete? This will lock this level for this coach.');
    if (!confirmed) return;
    try {
      setLockingLevel(true);
      const response = await fetch('/api/training-level-completion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerId: player.id,
          coachId: selectedCoach,
          sport: selectedSport || 'Badminton',
          trainingLevel,
          completed: true,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setIsLevelCompleted(true);
        toast.success('Level marked complete and locked for this coach.');
      } else {
        toast.error(result.message || 'Failed to mark level complete');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to mark level complete');
    } finally {
      setLockingLevel(false);
    }
  };

  // Bulk rating handlers
  const handleSelectItem = (skillId: string, checked: boolean) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(skillId);
      } else {
        newSet.delete(skillId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all items that are not marked as not applicable
      // Use getSelectableItems() which already filters correctly for both players and coaches
      const selectableItems = getSelectableItems();
      const selectableIds = selectableItems.map(item => item.id);
      console.log('[handleSelectAll] Selecting all items:', {
        totalItems: performanceItems.length,
        selectableCount: selectableItems.length,
        selectableIds: selectableIds
      });
      setSelectedItems(new Set(selectableIds));
    } else {
      console.log('[handleSelectAll] Clearing selection');
      setSelectedItems(new Set());
    }
  };

  const handleApplyBulkRating = () => {
    if (bulkRating === 0 || selectedItems.size === 0) return;
    
    setPerformanceItems(prev => 
      prev.map(item => 
        selectedItems.has(item.id)
          ? { ...item, rating: bulkRating }
          : item
      )
    );
    
    // Clear selection after applying
    setSelectedItems(new Set());
    setBulkRating(0);
  };

  // Get filtered items for bulk selection (respects any filters if added later)
  // Allow bulk rating for both players (self-evaluation) and coaches
  // Only filter by notApplicable - the edit conditions are already checked in the UI
  const getSelectableItems = () => {
    return performanceItems;
  };

  const isAllSelected = () => {
    const selectable = getSelectableItems();
    if (selectable.length === 0) return false;
    const allSelected = selectable.every(item => selectedItems.has(item.id));
    console.log('[isAllSelected]', {
      selectableCount: selectable.length,
      selectedCount: selectedItems.size,
      allSelected,
      selectableIds: selectable.map(i => i.id),
      selectedIds: Array.from(selectedItems)
    });
    return allSelected;
  };

  const isIndeterminate = () => {
    const selectable = getSelectableItems();
    const selectedCount = selectable.filter(item => selectedItems.has(item.id)).length;
    const isIndeterminateResult = selectedCount > 0 && selectedCount < selectable.length;
    console.log('[isIndeterminate]', {
      selectableCount: selectable.length,
      selectedCount,
      isIndeterminate: isIndeterminateResult
    });
    return isIndeterminateResult;
  };

  const checkDuplicateEntry = (period: string, dateRange: {start: string, end: string}, checkCoachId?: string) => {
    // Only check for SUBMITTED evaluations - saved evaluations can be submitted
    // IMPORTANT: Check for the SAME coachId (or self-evaluation if coachId is null/empty)
    // Multiple coaches can evaluate the same player for the same period
    // Player can do self-evaluation even if coaches have evaluated
    const currentCoachId =
      checkCoachId ||
      (selectedCoach === 'self' || selectedCoach === 'coach' ? undefined : selectedCoach) ||
      (coachId && coachId !== 'self' ? coachId : undefined);
    
    return previousEvaluations.some(evaluation => {
      const periodMatch = evaluation.period === period;
      const dateRangeMatch = compareDateRanges(evaluation.dateRange, dateRange);
      const submittedMatch = evaluation.submitted === true || evaluation.submitted === 'true' || evaluation.submitted === 1;
      
      // Match coachId: both null/undefined/empty means self-evaluation, or exact match
      const coachMatch = selectedCoach === 'coach'
        ? Boolean(evaluation.coachId)
        : ((!currentCoachId && !evaluation.coachId) ||
                         (currentCoachId && evaluation.coachId === currentCoachId) ||
           (currentCoachId === null && evaluation.coachId === null));
      
      return periodMatch && dateRangeMatch && submittedMatch && coachMatch;
    });
  };

  // Check if current period is submitted for a specific coach
  // Uses the same 4 parameters: coachId, period, dateRange (month), playerId
  const isCurrentPeriodSubmitted = (checkCoachId?: string) => {
    const currentDateRange = calculateDateRange(evaluationPeriod, evaluationYear);
    
    // First try coachEvaluations if coachId is provided
    let evaluationsToCheck = checkCoachId 
      ? (coachEvaluations[checkCoachId] || [])
      : previousEvaluations;
    
    // If coachEvaluations is empty but we have previousEvaluations with matching coachId, use those as fallback
    // This handles the case where coachEvaluations wasn't populated but previousEvaluations has the data
    if (checkCoachId && evaluationsToCheck.length === 0 && previousEvaluations.length > 0) {
      console.log('[isCurrentPeriodSubmitted] coachEvaluations empty, falling back to previousEvaluations filtered by coachId:', checkCoachId);
      // Filter by coachId - handle both string and null/undefined matching
      evaluationsToCheck = previousEvaluations.filter(e => {
        // Match if coachId is the same, or both are null/undefined/empty
        const coachMatch = e.coachId === checkCoachId || 
                          (!e.coachId && !checkCoachId) ||
                          (e.coachId === null && checkCoachId === null);
        console.log('[isCurrentPeriodSubmitted] Checking evaluation:', {
          id: e.id,
          evalCoachId: e.coachId,
          checkCoachId,
          coachMatch,
          period: e.period,
          submitted: e.submitted
        });
        return coachMatch;
      });
      console.log('[isCurrentPeriodSubmitted] After filtering by coachId:', {
        originalCount: previousEvaluations.length,
        filteredCount: evaluationsToCheck.length,
        filteredIds: evaluationsToCheck.map(e => ({ id: e.id, coachId: e.coachId, submitted: e.submitted }))
      });
    }
    
    // Only return true if there's a submitted evaluation (submitted === true)
    const result = evaluationsToCheck.some(evaluation => {
      const periodMatch = evaluation.period === evaluationPeriod;
      const dateRangeMatch = compareDateRanges(evaluation.dateRange, currentDateRange);
      const submittedMatch = evaluation.submitted === true || evaluation.submitted === 'true' || evaluation.submitted === 1;
      
      return periodMatch && dateRangeMatch && submittedMatch;
    });
    
    console.log('[isCurrentPeriodSubmitted] Check result:', {
      checkCoachId,
      currentPeriod: evaluationPeriod,
      currentDateRange,
      evaluationsToCheckCount: evaluationsToCheck.length,
      result,
      source: checkCoachId && coachEvaluations[checkCoachId]?.length > 0 ? 'coachEvaluations' : 'previousEvaluations'
    });
    
    return result;
  };

  const currentPeriodStatus = isCurrentPeriodSubmitted(
    selectedCoach === 'self' || selectedCoach === 'coach' ? undefined : selectedCoach
  ) ? 'submitted' : 'pending';
  
  // Check if a specific coach has submitted for current period
  const getCoachSubmissionStatus = (coachIdToCheck: string) => {
    return isCurrentPeriodSubmitted(coachIdToCheck === 'self' ? undefined : coachIdToCheck);
  };

  const handleSave = async () => {
    if (!player?.id) {
      console.error('[handleSave] Player missing');
      toast.error('Player information is missing');
      return;
    }

    if (!performanceItems || performanceItems.length === 0) {
      console.error('[handleSave] No performance items');
      toast.error('No performance data to save');
      return;
    }

    if (selectedEvaluation !== 'current') {
      console.error('[handleSave] Not current evaluation');
      toast.error('Please select current evaluation to save');
      return;
    }

    if (!canEdit) {
      console.error('[handleSave] User does not have permission to save');
      toast.error('You do not have permission to save this evaluation');
      return;
    }

    const deltaSummary = getRatingDeltaSummary(performanceItems);
    const changedItems = getRatingChangeItems(performanceItems);
    if (deltaSummary.decreased > 0) {
      const proceed = window.confirm(
        `Warning: ${deltaSummary.decreased} skill rating(s) decreased from previous save.\n\n` +
        `Increased: ${deltaSummary.increased}\n` +
        `No Changes: ${deltaSummary.same}\n` +
        `Decreased: ${deltaSummary.decreased}\n\n` +
        `Continue save?`
      );
      if (!proceed) return;
    }

    setSaving(true);
    setError(null);
    try {
      const isMasterTraining = assessmentType === 'master-training';
      const today = new Date();
      const todayDate = today.toISOString().split('T')[0];
      const currentDateRange = isMasterTraining
        ? { start: todayDate, end: todayDate }
        : calculateDateRange(evaluationPeriod, evaluationYear);
      
      const requestBody = {
        playerId: player.id,
        sport: selectedSport || 'Badminton',
        assessmentType: assessmentType,
        trainingLevel: trainingLevel || null,
        evaluationPeriod: isMasterTraining ? 'continuous' : evaluationPeriod,
        year: isMasterTraining ? today.getFullYear() : evaluationYear,
        dateRange: currentDateRange,
        items: performanceItems
          .map(item => ({
            name: item.name,
            category: item.category,
            rating: item.rating,
            notes: item.notes || '',
            areaOfImprovement: item.areaOfImprovement && item.areaOfImprovement.length > 0
              ? item.areaOfImprovement
              : [defaultGuidanceOption],
            suggestedPractices: item.suggestedPractices && item.suggestedPractices.length > 0
              ? item.suggestedPractices
              : [defaultGuidanceOption]
          })),
        notes: notes,
        coachId: selectedCoach === 'self' || selectedCoach === 'coach' ? null : selectedCoach,
        submitted: false // Save without submitting
      };

      console.log('[Save] Request body:', {
        playerId: requestBody.playerId,
        sport: requestBody.sport,
        assessmentType: requestBody.assessmentType,
        itemsCount: requestBody.items.length
      });

      // Save to backend
      const response = await fetch('/api/performance/basic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: `HTTP error! status: ${response.status}` };
        }
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const nowIso = new Date().toISOString();
        const comparableItems = performanceItems.filter((item) => !item.notApplicable);
        const currentTotalScore = comparableItems.reduce((sum, item) => sum + Number(item.rating || 0), 0);
        const previousTotalScore = comparableItems.reduce(
          (sum, item) => sum + Number(savedRatingsSnapshot?.[getItemKey(item)] || 0),
          0
        );
        const maxTotalScore = comparableItems.length * 5;
        const improvementComparableItems = comparableItems.filter((item) => {
          const prev = Number(savedRatingsSnapshot?.[getItemKey(item)] || 0);
          const curr = Number(item.rating || 0);
          return prev > 0 || curr > 0;
        });
        const currentComparableScore = improvementComparableItems.reduce((sum, item) => sum + Number(item.rating || 0), 0);
        const previousComparableScore = improvementComparableItems.reduce(
          (sum, item) => sum + Number(savedRatingsSnapshot?.[getItemKey(item)] || 0),
          0
        );
        const improvementRatePct = previousComparableScore > 0
          ? ((currentComparableScore - previousComparableScore) / previousComparableScore) * 100
          : (currentTotalScore > 0 ? 100 : 0);
        const totalScorePct = maxTotalScore > 0 ? (currentTotalScore / maxTotalScore) * 100 : 0;
        const daysSincePreviousSave = savedSnapshotAt
          ? Math.max(
              0,
              Math.round(
                (new Date(nowIso).getTime() - new Date(savedSnapshotAt).getTime()) / (1000 * 60 * 60 * 24)
              )
            )
          : null;
        await persistSkillProgressSnapshot(performanceItems);
        setLastSaveSummary({
          increased: deltaSummary.increased,
          decreased: deltaSummary.decreased,
          same: deltaSummary.same,
          daysSincePreviousSave,
          currentSavedAt: nowIso,
          previousTotalScore,
          currentTotalScore,
          maxTotalScore,
          improvementRatePct,
          totalScorePct,
        });
        setLastSaveChangedItems(changedItems);
        setSavedRatingsSnapshot(deltaSummary.currentSnapshot);
        setSavedSnapshotAt(nowIso);
        // Reload evaluations in background
        await loadPreviousEvaluations();
        // Reload performance data to show saved values - wait for API to process
        // Don't reset isSubmitted - let loadPerformanceData check and set it correctly
        if (assessmentItems.length > 0) {
          // Wait longer to ensure database transaction is committed
          setTimeout(() => {
            console.log('[BasicPerformance] Reloading performance data after save...');
            loadPerformanceData();
          }, 1500);
        }
      } else {
        console.error('[Save] Error response:', data);
        toast.error(data.message || 'Failed to save evaluation');
        setError(data.message || 'Failed to save evaluation');
      }
    } catch (error: any) {
      console.error('Exception in handleSave:', error);
      toast.error('An error occurred while saving: ' + (error.message || 'Unknown error'));
      setError('An error occurred while saving: ' + (error.message || 'Unknown error'));
    } finally {
      // Always reset saving state, even if there was an error
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    console.log('[handleSubmit] Called with state:', {
      playerId: player?.id,
      performanceItemsCount: performanceItems?.length,
      selectedEvaluation,
      isSelfEvaluation,
      selectedSport,
      assessmentType
    });
    
    // Validate prerequisites
    if (!player || !player.id) {
      console.error('[handleSubmit] Player missing');
      toast.error('Player information is missing');
      return;
    }

    if (!performanceItems || performanceItems.length === 0) {
      console.error('[handleSubmit] No performance items');
      toast.error('No performance items to submit');
      return;
    }

    if (selectedEvaluation !== 'current') {
      console.error('[handleSubmit] Not current evaluation');
      toast.error('Please select current evaluation to submit');
      return;
    }

    if (!canEdit) {
      console.error('[handleSubmit] User does not have permission to submit');
      toast.error('You do not have permission to submit this evaluation');
      return;
    }

    const deltaSummary = getRatingDeltaSummary(performanceItems);
    const changedItems = getRatingChangeItems(performanceItems);
    if (deltaSummary.decreased > 0) {
      const proceed = window.confirm(
        `Warning: ${deltaSummary.decreased} skill rating(s) decreased from previous save.\n\n` +
        `Increased: ${deltaSummary.increased}\n` +
        `No Changes: ${deltaSummary.same}\n` +
        `Decreased: ${deltaSummary.decreased}\n\n` +
        `Continue submit?`
      );
      if (!proceed) return;
    }

    setLoading(true);
    try {
      // Calculate date range
      const dateRangeToUse = calculateDateRange(evaluationPeriod, evaluationYear);
      
      if (!dateRangeToUse.start || !dateRangeToUse.end) {
        toast.error('Invalid date range');
        setLoading(false);
        return;
      }

      // Check for duplicate SUBMITTED entry for THIS coach/self (saved evaluations can be submitted)
      // Determine the coachId for the current submission
      const submitCoachId = isSelfEvaluation ? undefined : (coachId || selectedCoach);
      if (checkDuplicateEntry(evaluationPeriod, dateRangeToUse, submitCoachId)) {
        const coachType = isSelfEvaluation ? 'self-evaluation' : 'coach evaluation';
        toast.error(`A submitted ${coachType} for ${evaluationPeriod} already exists for this period!`);
        setLoading(false);
        return;
      }

      // Prepare request body
      const requestBody = {
        playerId: player.id,
        coachId: coachId || '',
        evaluationPeriod: evaluationPeriod,
        dateRange: dateRangeToUse,
        items: performanceItems,
        notes: notes || '',
        sequenceNumber: sequenceCounter,
        assessmentType: assessmentType || 'high-level',
        sport: selectedSport || 'Badminton', // Include sport for sport-specific table
        submitted: true // Submit button should mark as submitted
      };
      
      console.log('[Submit] Request body:', {
        playerId: requestBody.playerId,
        sport: requestBody.sport,
        assessmentType: requestBody.assessmentType,
        itemsCount: requestBody.items.length,
        dateRange: requestBody.dateRange
      });

      // Submit to backend
      const response = await fetch('/api/performance/basic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Error: ${response.status} ${response.statusText}`;
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage = errorResult.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        console.error('API Error:', errorMessage);
        toast.error(errorMessage);
        setLoading(false);
        return;
      }

      const result = await response.json();
      
      if (result.success) {
        const nowIso = new Date().toISOString();
        const comparableItems = performanceItems.filter((item) => !item.notApplicable);
        const currentTotalScore = comparableItems.reduce((sum, item) => sum + Number(item.rating || 0), 0);
        const previousTotalScore = comparableItems.reduce(
          (sum, item) => sum + Number(savedRatingsSnapshot?.[getItemKey(item)] || 0),
          0
        );
        const maxTotalScore = comparableItems.length * 5;
        const improvementComparableItems = comparableItems.filter((item) => {
          const prev = Number(savedRatingsSnapshot?.[getItemKey(item)] || 0);
          const curr = Number(item.rating || 0);
          return prev > 0 || curr > 0;
        });
        const currentComparableScore = improvementComparableItems.reduce((sum, item) => sum + Number(item.rating || 0), 0);
        const previousComparableScore = improvementComparableItems.reduce(
          (sum, item) => sum + Number(savedRatingsSnapshot?.[getItemKey(item)] || 0),
          0
        );
        const improvementRatePct = previousComparableScore > 0
          ? ((currentComparableScore - previousComparableScore) / previousComparableScore) * 100
          : (currentTotalScore > 0 ? 100 : 0);
        const totalScorePct = maxTotalScore > 0 ? (currentTotalScore / maxTotalScore) * 100 : 0;
        const daysSincePreviousSave = savedSnapshotAt
          ? Math.max(
              0,
              Math.round(
                (new Date(nowIso).getTime() - new Date(savedSnapshotAt).getTime()) / (1000 * 60 * 60 * 24)
              )
            )
          : null;
        await persistSkillProgressSnapshot(performanceItems);
        // EXACT same pattern as RankingEvaluationWithTabs - just set isSubmitted, don't reload anything
        setIsSubmitted(true);
        setLastSaveSummary({
          increased: deltaSummary.increased,
          decreased: deltaSummary.decreased,
          same: deltaSummary.same,
          daysSincePreviousSave,
          currentSavedAt: nowIso,
          previousTotalScore,
          currentTotalScore,
          maxTotalScore,
          improvementRatePct,
          totalScorePct,
        });
        setLastSaveChangedItems(changedItems);
        setSavedRatingsSnapshot(deltaSummary.currentSnapshot);
        setSavedSnapshotAt(nowIso);
        setLoading(false);
        // Do NOT reload evaluations or performance data - just set isSubmitted
        // The data will refresh naturally when period changes or component remounts
        // This prevents any refresh that might reset isSubmitted
      } else {
        console.error('API returned success:false:', result.message);
        toast.error(result.message || 'Failed to submit performance evaluation');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Exception in handleSubmit:', error);
      toast.error(error?.message || 'Failed to submit performance evaluation. Check console for details.');
      setLoading(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setNotes('');
    setSelectedEvaluation('current');
    loadPerformanceData();
    setCurrentDateRange(calculateDateRange(evaluationPeriod, evaluationYear));
  };

  const handleEvaluationChange = (evaluationId: string) => {
    setSelectedEvaluation(evaluationId);
  };

  const generateChartData = (period: string) => {
    const filteredEvaluations = previousEvaluations.filter(evaluation => evaluation.period === period);
    
    // Create data points for each evaluation
    return filteredEvaluations.map(evaluation => {
      const dataPoint: any = {
        sequence: evaluation.sequenceNumber,
        date: evaluation.submittedAt,
        period: evaluation.period,
        displayDate: new Date(evaluation.submittedAt).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
      };
      
      // Add rating for each skill
      predefinedItems.forEach((item, index) => {
        const skillItem = evaluation.items.find(item => item.name === predefinedItems[index].name);
        dataPoint[item.name] = skillItem?.rating || 0;
      });
      
      return dataPoint;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()); // Sort by actual date
  };

  const renderSingleChart = () => {
    const quarterlyData = generateChartData('quarterly');
    const yearlyData = generateChartData('yearly');

    const currentData = activeTab === 0 ? quarterlyData : yearlyData;
    const periodName = activeTab === 0 ? 'Quarterly' : 'Yearly';

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          All Skills Performance Trends - {periodName}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label={`Quarterly (${quarterlyData.length} records)`} />
            <Tab label={`Yearly (${yearlyData.length} records)`} />
          </Tabs>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowAllSkills(!showAllSkills)}
            sx={{ ml: 2 }}
          >
            {showAllSkills ? 'Show Top 10' : 'Show All 20'}
          </Button>
        </Box>
        
        <Card>
          <CardContent>
            <ResponsiveContainer width="100%" height={600}>
              <LineChart data={currentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="displayDate" 
                  label={{ value: 'Date', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  domain={[0, 5]}
                  ticks={[0, 1, 2, 3, 4, 5]}
                  label={{ value: 'Rating (0-5)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                />
                <RechartsTooltip 
                  formatter={(value: any, name: string) => [`${value}/5`, name]}
                  labelFormatter={(label: any) => `Date: ${label}`}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                />
                {/* Generate lines for all 20 skills, controlling visibility */}
                {predefinedItems.map((item, index) => {
                  const colors = [
                    '#9C27B0', '#2196F3', '#4CAF50', '#FF9800', '#F44336',
                    '#E91E63', '#00BCD4', '#8BC34A', '#FFC107', '#795548',
                    '#607D8B', '#3F51B5', '#009688', '#CDDC39', '#FF5722',
                    '#9E9E9E', '#673AB7', '#00BCD4', '#4CAF50', '#FF9800'
                  ];
                  const isVisible = showAllSkills || index < 10;
                  return (
                    <Line 
                      key={item.name}
                      type="monotone" 
                      dataKey={item.name}
                      stroke={colors[index % colors.length]}
                      strokeWidth={isVisible ? 2 : 0}
                      strokeOpacity={isVisible ? 1 : 0}
                      dot={isVisible ? { 
                        fill: colors[index % colors.length], 
                        strokeWidth: 2, 
                        stroke: colors[index % colors.length],
                        r: 4 
                      } : false}
                      activeDot={isVisible ? { 
                        r: 6, 
                        stroke: colors[index % colors.length], 
                        strokeWidth: 2,
                        fill: colors[index % colors.length]
                      } : false}
                      name={item.name}
                      connectNulls={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string): number[] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  // Helper function to get smiley emoji that matches Material-UI icons
  const getSmileyEmoji = (rating: number): string => {
    // Use Unicode emoji that visually match Material-UI sentiment icons
    if (rating <= 1) return '😢'; // VeryBadIcon - crying face
    if (rating <= 2) return '😕'; // BadIcon - slightly frowning
    if (rating <= 3) return '😐'; // NeutralIcon - neutral face
    if (rating <= 4) return '😊'; // GoodIcon - smiling
    return '😄'; // ExcellentIcon - big smile
  };

  // Cache for icon images
  const iconImageCache: Map<number, string> = new Map();

  // Pre-generate all icon images
  const generateIconImages = async (): Promise<void> => {
    const ratings = [1, 2, 3, 4, 5];
    
    try {
      const { createRoot } = await import('react-dom/client');

      // Generate each icon separately to avoid conflicts
      for (const rating of ratings) {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.top = '-9999px';
        container.style.width = '48px'; // Larger container for better quality
        container.style.height = '48px';
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';
        container.style.backgroundColor = 'white';
        document.body.appendChild(container);

        const IconComponent = rating <= 1 ? VeryBadIcon : 
                             rating <= 2 ? BadIcon : 
                             rating <= 3 ? NeutralIcon : 
                             rating <= 4 ? GoodIcon : 
                             ExcellentIcon;
        
        const iconColor = rating <= 1 ? '#f44336' : 
                         rating <= 2 ? '#ff9800' : 
                         rating <= 3 ? '#ffc107' : 
                         rating <= 4 ? '#4caf50' : '#2196f3';

        const root = createRoot(container);
        root.render(
          React.createElement(IconComponent, { 
            sx: { 
              fontSize: 40, // Much larger for thicker, filled appearance
              color: iconColor,
              fontWeight: 'bold' // Make it bolder
            }
          })
        );

        // Wait for icon to render
        await new Promise(resolve => setTimeout(resolve, 150));

        // Capture icon with higher quality
        const canvas = await html2canvas(container, {
          width: 48,
          height: 48,
          backgroundColor: 'white',
          scale: 3, // Higher scale for better quality
          logging: false,
          useCORS: true
        });
        
        iconImageCache.set(rating, canvas.toDataURL('image/png'));

        // Cleanup
        root.unmount();
        document.body.removeChild(container);
      }
    } catch (error) {
      console.error('Error generating icon images:', error);
    }
  };

  // Helper function to add footer with branding
  const addFooter = (doc: jsPDF, pageWidth: number, pageHeight: number, footerHeight: number) => {
    const footerY = pageHeight - footerHeight;
    
    // Draw footer line
    doc.setLineWidth(0.5);
    doc.line(10, footerY, pageWidth - 10, footerY);
    
    // Footer branding
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Encycdata', 10, footerY + 5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('EncycGames', 10, footerY + 10);
    
    // Page number
    const pageNum = doc.getCurrentPageInfo().pageNumber;
    const totalPages = doc.getNumberOfPages();
    doc.setFontSize(8);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, footerY + 7, { align: 'center' });
    
    // Date
    const footerDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    doc.text(footerDate, pageWidth - 10, footerY + 7, { align: 'right' });
  };

  // Generate PDF Report - Table format matching UI
  const generatePDFReport = async () => {
    // Show loading toast
    toast.info('Generating PDF with icons...', { autoClose: 2000 });
    
    // Pre-generate all icon images
    await generateIconImages();
    
    // Get data from submitted evaluation if viewing one, otherwise use current data
    let reportData = {
      items: performanceItems,
      notes: notes,
      period: evaluationPeriod,
      year: evaluationYear,
      dateRange: currentDateRange,
      assessmentType: assessmentType
    };
    
    // Determine coach name for the report
    let reportCoachName = coachName;
    
    // If viewing a submitted evaluation, use that data and get coach name from evaluation
    if (selectedEvaluation !== 'current') {
      const selectedEvalData = previousEvaluations.find(evaluation => evaluation.id === selectedEvaluation);
      if (selectedEvalData) {
        // If this is a coach evaluation, get coach name from coachOptions or load it
        if (selectedEvalData.coachId && selectedEvalData.coachId !== 'self') {
          const coachFromOptions = coachOptions.find((c: any) => c.id === selectedEvalData.coachId);
          if (coachFromOptions && coachFromOptions.name) {
            reportCoachName = coachFromOptions.name;
          } else {
            // Try to fetch coach name
            try {
              const coachResponse = await fetch('/api/coaches');
              const coachData = await coachResponse.json();
              if (coachData.success && coachData.data) {
                const coach = coachData.data.find((c: any) => c.id === selectedEvalData.coachId);
                if (coach) {
                  // Use first_name/last_name from coaches table OR user.firstName/user.lastName
                  reportCoachName = `${coach.first_name || coach.user?.firstName || ''} ${coach.last_name || coach.user?.lastName || ''}`.trim() || coach.coach_id_code || 'Unknown Coach';
                }
              }
            } catch (err) {
              console.error('Error loading coach name for report:', err);
            }
          }
        } else {
          reportCoachName = 'Self Evaluation';
        }
        // CRITICAL: Verify assessmentType matches
        // Handle both old ('quick'/'advanced') and new ('high-level'/'low-level') values
        const evalAssessmentType = selectedEvalData.assessmentType || 'high-level';
        const normalizedEvalType = evalAssessmentType === 'quick' ? 'high-level' : 
                                   (evalAssessmentType === 'advanced' || evalAssessmentType === 'low-level') ? 'master-training' : 
                                   evalAssessmentType;
        if (normalizedEvalType !== assessmentType) {
          console.error(`[PDF Report] AssessmentType mismatch! Cannot generate report. Eval: ${evalAssessmentType} (normalized: ${normalizedEvalType}), Current: ${assessmentType}`);
          toast.error(`Cannot generate report: Evaluation type mismatch`);
          return;
        }
        
        console.log('[PDF Report] Using submitted evaluation data:', {
          evaluationId: selectedEvalData.id,
          itemsCount: selectedEvalData.items?.length || 0,
          assessmentType: evalAssessmentType
        });
        
        reportData = {
          items: selectedEvalData.items || [],
          notes: selectedEvalData.notes,
          period: selectedEvalData.period,
          year: new Date(selectedEvalData.dateRange.start).getFullYear(),
          dateRange: selectedEvalData.dateRange,
          assessmentType: evalAssessmentType
        };
      }
    }
    
    // If no items, show error
    if (!reportData.items || reportData.items.length === 0) {
      console.error('[PDF Report] No items available:', {
        selectedEvaluation,
        performanceItemsCount: performanceItems.length,
        reportDataItemsCount: reportData.items?.length || 0
      });
      toast.error('No evaluation data available to generate report');
      return;
    }
    
    console.log('[PDF Report] Generating report with:', {
      itemsCount: reportData.items.length,
      assessmentType: reportData.assessmentType,
      categories: [...new Set(reportData.items.map((i: BasicPerformanceItem) => i.category))]
    });
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const footerHeight = 15;
    const headerHeight = 20;
    let yPosition = headerHeight;
    const rowHeight = 7;
    const tableStartX = margin;
    let tableStartY = margin;

    // Header with branding
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Encycdata', margin, 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('EncycGames', margin, 13);
    
    // Get sport name for title and filename
    const sportName = selectedSport || 'Badminton';
    
    // Assessment title with sport name
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `${sportName} - ${reportData.assessmentType === 'master-training' ? 'Master Data Training' : 'High-level'} Assessment`,
      pageWidth / 2,
      yPosition,
      { align: 'center' }
    );
    yPosition += 8;

    // Header Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const playerName = `${player?.firstName || player?.user?.firstName || ''} ${player?.lastName || player?.user?.lastName || ''}`.trim();
    const academyName = player?.academyName || 'N/A';
    const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    doc.text(`Player: ${playerName}`, margin, yPosition);
    doc.text(`Coach: ${reportCoachName || 'N/A'}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Academy: ${academyName}`, margin, yPosition);
    doc.text(`Date: ${reportDate}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Period: ${reportData.dateRange.start} to ${reportData.dateRange.end} (${getPeriodLabel(reportData.period)} ${reportData.year})`, margin, yPosition);
    yPosition += 8;

    // Rating Scale Reference Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Rating Scale Reference (For Information Only):', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Calculate the maximum width of level names for alignment
    const levelNames = [
      'Beginner (1-2):',
      'Intermediate (2-3):',
      'Advanced (3-4):',
      'Master (4-5):'
    ];
    doc.setFont('helvetica', 'bold');
    const maxLevelWidth = Math.max(...levelNames.map(name => doc.getTextWidth(name)));
    const definitionStartX = margin + 8 + maxLevelWidth + 5; // 8 for colored box + max width + 5 for space
    
    // Beginner (1-2)
    doc.setFillColor(244, 67, 54); // Red
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Beginner (1-2):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Learning basic rules and shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Intermediate (2-3)
    doc.setFillColor(255, 152, 0); // Orange
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Intermediate (2-3):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can perform upto 5 consistent strokes but lacking accuracy and perfection', definitionStartX, yPosition);
    yPosition += 5;
    
    // Advanced (3-4)
    doc.setFillColor(33, 150, 243); // Blue
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Advanced (3-4):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can play 15 or more consistent strokes and do long rallies with different shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Master (4-5)
    doc.setFillColor(76, 175, 80); // Green
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Master (4-5):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Aggressive, Accurate and consistently performing in varying conditions', definitionStartX, yPosition);
    yPosition += 8;

    // Table column widths - reduced skill column, increased performance column
    const colWidths = {
      skill: (pageWidth - 2 * margin) * 0.38, // Reduced from 0.45
      notApplicable: (pageWidth - 2 * margin) * 0.10,
      rating: (pageWidth - 2 * margin) * 0.18,
      performance: (pageWidth - 2 * margin) * 0.27, // Increased from 0.20 to fit "Needs Improvement"
      smiley: (pageWidth - 2 * margin) * 0.07
    };
    
    const drawTableRow = (x: number, y: number, lineWidth: number = 0.2) => {
      doc.setLineWidth(lineWidth);
      let currentX = x;
      const lineY = y + rowHeight;
      const tableEndX = x + (pageWidth - 2 * margin);
      
      // Vertical lines
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.skill;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.notApplicable;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.rating;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.performance;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.smiley;
      doc.line(currentX, y, currentX, lineY);
      
      // Horizontal line at bottom of row
      doc.line(x, lineY, tableEndX, lineY);
    };

    const drawSectionBorder = (x: number, y: number, height: number, lineWidth: number = 0.8) => {
      doc.setLineWidth(lineWidth);
      const endX = x + (pageWidth - 2 * margin);
      const endY = y + height;
      
      // Draw thick outer border for section
      doc.line(x, y, endX, y); // Top
      doc.line(endX, y, endX, endY); // Right
      doc.line(endX, endY, x, endY); // Bottom
      doc.line(x, endY, x, y); // Left
    };

    const drawTableHeader = (y: number) => {
      const headerY = y;
      const headerBottomY = y + rowHeight;
      
      // Draw header background
      doc.setFillColor(245, 245, 245);
      doc.rect(tableStartX, headerY, pageWidth - 2 * margin, rowHeight, 'F');
      
      // Draw top border for header
      doc.setLineWidth(0.5);
      doc.line(tableStartX, headerY, tableStartX + (pageWidth - 2 * margin), headerY);
      
      // Draw borders with consistent thickness (header slightly thicker)
      drawTableRow(tableStartX, headerY, 0.3);
      
      // Header text - centered in cells
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let textX = tableStartX;
      doc.text('Skill', textX + colWidths.skill / 2, headerY + 5, { align: 'center' });
      textX += colWidths.skill;
      doc.text('N/A', textX + colWidths.notApplicable / 2, headerY + 5, { align: 'center' });
      textX += colWidths.notApplicable;
      doc.text('Rating', textX + colWidths.rating / 2, headerY + 5, { align: 'center' });
      textX += colWidths.rating;
      doc.text('Performance', textX + colWidths.performance / 2, headerY + 5, { align: 'center' });
      textX += colWidths.performance;
      doc.text('Smiley', textX + colWidths.smiley / 2, headerY + 5, { align: 'center' });
    };

    const drawTableDataRow = (item: BasicPerformanceItem, y: number, categoryColor?: { bg: number[], text: number[] }) => {
      const rowY = y;
      const rowBottomY = y + rowHeight;
      
      // Draw borders FIRST (before background fill) to ensure they're visible
      drawTableRow(tableStartX, rowY, 0.2);
      
      // Draw row background if category color provided (after borders so borders show)
      if (categoryColor) {
        doc.setFillColor(categoryColor.bg[0], categoryColor.bg[1], categoryColor.bg[2]);
        doc.rect(tableStartX, rowY, pageWidth - 2 * margin, rowHeight, 'F');
        // Redraw borders on top of background to ensure visibility
        drawTableRow(tableStartX, rowY, 0.2);
      }
      
      // Data text
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      let textX = tableStartX + 2;
      
      // Skill name
      const skillName = doc.splitTextToSize(item.name, colWidths.skill - 4);
      doc.text(skillName, textX, rowY + 5);
      
      // Not Applicable - show as text only (no box)
      textX += colWidths.skill + 2;
      if (item.notApplicable) {
        doc.text('N/A', textX, rowY + 5);
      } else {
        doc.text('-', textX, rowY + 5); // Show dash when applicable
      }
      
      // Rating - draw visual circles for stars (filled and empty)
      textX += colWidths.notApplicable + 2;
      if (!item.notApplicable && item.rating > 0) {
        const circleRadius = 2;
        const circleSpacing = 5;
        const circleY = rowY + 3.5; // Center vertically in row
        
        // Draw filled circles for rating
        for (let i = 0; i < item.rating; i++) {
          const circleX = textX + (i * circleSpacing);
          doc.setFillColor(0, 0, 0); // Black filled
          doc.circle(circleX, circleY, circleRadius, 'F'); // Filled circle
        }
        
        // Draw empty circles for remaining
        for (let i = item.rating; i < 5; i++) {
          const circleX = textX + (i * circleSpacing);
          doc.setDrawColor(0, 0, 0); // Black outline
          doc.setLineWidth(0.3);
          doc.circle(circleX, circleY, circleRadius, 'S'); // Stroked (empty) circle
        }
      } else if (item.notApplicable) {
        doc.text('N/A', textX, rowY + 5);
      } else {
        doc.text('-', textX, rowY + 5); // Show dash for unrated
      }
      
      // Performance level - only show if rating is filled
      textX += colWidths.rating + 2;
      if (item.notApplicable) {
        doc.text('N/A', textX, rowY + 5);
      } else if (item.rating > 0) {
        const performance = getPerformanceLevel(item.rating);
        const perfColor = hexToRgb(performance.color);
        doc.setTextColor(perfColor[0], perfColor[1], perfColor[2]);
        // Now we have more space, can show full text
        const perfTextLines = doc.splitTextToSize(performance.text, colWidths.performance - 2);
        doc.text(perfTextLines, textX, rowY + 5);
        doc.setTextColor(0, 0, 0); // Reset to black
      }
      // If rating is 0, leave blank (don't show "Not Rated")
      
      // Smiley - use Material-UI icon image from cache, centered in column
      // Calculate smiley column start position correctly (without accumulated padding)
      const smileyColumnStartX = tableStartX + colWidths.skill + colWidths.notApplicable + colWidths.rating + colWidths.performance;
      
      if (!item.notApplicable && item.rating > 0) {
        const iconImage = iconImageCache.get(item.rating);
        if (iconImage) {
          // Calculate icon size to fit within column (with padding)
          const availableWidth = colWidths.smiley - 2; // Leave 1mm padding on each side
          const availableHeight = rowHeight - 2; // Leave 1mm padding top/bottom
          const iconSize = Math.min(availableWidth, availableHeight, 5); // Max 5mm, but fit in cell
          
          // Calculate centered position in smiley column
          const columnCenterX = smileyColumnStartX + (colWidths.smiley / 2);
          const iconX = columnCenterX - (iconSize / 2); // Center horizontally
          const iconY = rowY + (rowHeight / 2) - (iconSize / 2); // Center vertically
          
          // Validate icon fits within column boundaries
          const iconRight = iconX + iconSize;
          const columnRight = smileyColumnStartX + colWidths.smiley;
          const iconBottom = iconY + iconSize;
          const rowBottom = rowY + rowHeight;
          
          // Ensure icon doesn't overflow
          if (iconX >= smileyColumnStartX && iconRight <= columnRight && iconY >= rowY && iconBottom <= rowBottom) {
            // Add icon image to PDF (centered and validated)
            doc.addImage(iconImage, 'PNG', iconX, iconY, iconSize, iconSize);
          } else {
            // Fallback if validation fails - use smaller size
            const safeIconSize = Math.min(colWidths.smiley - 2, rowHeight - 2);
            const safeIconX = smileyColumnStartX + (colWidths.smiley / 2) - (safeIconSize / 2);
            const safeIconY = rowY + (rowHeight / 2) - (safeIconSize / 2);
            doc.addImage(iconImage, 'PNG', safeIconX, safeIconY, safeIconSize, safeIconSize);
          }
        } else {
          // Fallback to emoji if icon not cached
          const smileyEmoji = getSmileyEmoji(item.rating);
          const columnCenterX = smileyColumnStartX + (colWidths.smiley / 2);
          doc.setFontSize(12);
          const textWidth = doc.getTextWidth(smileyEmoji);
          doc.text(smileyEmoji, columnCenterX - (textWidth / 2), rowY + 5);
          doc.setFontSize(8);
        }
      }
    };

    // Start table
    tableStartY = yPosition;
    drawTableHeader(tableStartY);
    yPosition += rowHeight;

    // Assessment Data
    if (reportData.assessmentType === 'master-training') {
      // Group by category for advanced assessment
      const groupedItems = reportData.items.reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
      }, {} as { [key: string]: BasicPerformanceItem[] });
      
      // Debug: Log grouped items to verify all items are present
      console.log('[PDF Report] Grouped items by category:');
      Object.entries(groupedItems).forEach(([category, items]) => {
        console.log(`  ${category}: ${items.length} items`);
      });

      Object.entries(groupedItems).forEach(([category, items]) => {
        // Check if we need a new page
        if (yPosition + rowHeight * 2 > pageHeight - footerHeight) {
          // Add footer to current page
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
        }

        // Category header row - merged row (no column lines, just outer border)
        yPosition += 2; // Add spacing before category
        const categoryStartY = yPosition;
        const categoryColor = getCategoryColor(category);
        const bgColor = hexToRgb(categoryColor.bg);
        const textColor = hexToRgb(categoryColor.text);
        
        // Draw category background (full width, no internal column lines)
        doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
        
        // Only draw top and bottom horizontal lines for category header (no vertical lines)
        doc.setLineWidth(0.5);
        doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition); // Top
        doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight); // Bottom
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        yPosition += rowHeight;

        // Items in category - track section height for outer border
        items.forEach((item) => {
          if (yPosition + rowHeight > pageHeight - footerHeight) {
            // Draw section border before page break
            const sectionHeight = yPosition - categoryStartY;
            drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
            
            // Add footer to current page
            addFooter(doc, pageWidth, pageHeight, footerHeight);
            doc.addPage();
            yPosition = headerHeight;
            tableStartY = yPosition;
            drawTableHeader(tableStartY);
            yPosition += rowHeight;
            
            // Start new section on new page
            const newCategoryStartY = yPosition;
            yPosition += 2;
            doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
            doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
            doc.setLineWidth(0.5);
            doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
            doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(textColor[0], textColor[1], textColor[2]);
            doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
            doc.setTextColor(0, 0, 0);
            yPosition += rowHeight;
          }

          drawTableDataRow(item, yPosition, { bg: bgColor, text: textColor });
          yPosition += rowHeight;
        });
        
        // Calculate average score for this section
        const ratedItems = items.filter(item => !item.notApplicable && item.rating > 0);
        const averageScore = ratedItems.length > 0
          ? (ratedItems.reduce((sum, item) => sum + item.rating, 0) / ratedItems.length).toFixed(2)
          : 'N/A';
        
        // Add average score row before notes
        if (yPosition + rowHeight * 2 > pageHeight - footerHeight) {
          // Draw section border before page break
          const sectionHeight = yPosition - categoryStartY;
          drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
          
          // Add footer to current page
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
        }
        
        // Average score row
        doc.setLineWidth(0.3);
        doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`Average Score: ${averageScore}`, tableStartX + 5, yPosition + 5);
        yPosition += rowHeight;
        
        // Notes section (5 lines)
        // For submitted evaluations, section notes might not be available separately
        // Use current sectionNotes if available, otherwise empty
        const notesText = sectionNotes[category] || '';
        const notesLines = 5;
        const notesLineHeight = 4;
        
        if (yPosition + notesLines * notesLineHeight > pageHeight - footerHeight) {
          // Draw section border before page break
          const sectionHeight = yPosition - categoryStartY;
          drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
          
          // Add footer to current page
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
        }
        
        // Draw notes box
        doc.setLineWidth(0.3);
        doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, notesLines * notesLineHeight);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes/Comments:', tableStartX + 2, yPosition + 3);
        
        // Split notes text into lines
        const maxWidth = pageWidth - 2 * margin - 4;
        const words = notesText.split(' ');
        let currentLine = '';
        let lineY = yPosition + 6;
        let lineCount = 0;
        
        words.forEach((word) => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const textWidth = doc.getTextWidth(testLine);
          if (textWidth > maxWidth && currentLine) {
            if (lineCount < notesLines) {
              doc.setFontSize(8);
              doc.setFont('helvetica', 'normal');
              doc.text(currentLine, tableStartX + 2, lineY);
              lineY += notesLineHeight;
              lineCount++;
            }
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        });
        if (currentLine && lineCount < notesLines) {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(currentLine, tableStartX + 2, lineY);
          lineCount++;
        }
        
        // Fill remaining lines with blank space
        while (lineCount < notesLines) {
          lineY += notesLineHeight;
          lineCount++;
        }
        
        yPosition += notesLines * notesLineHeight;
        
        // Draw thick outer border for entire section (category header + all items + average + notes)
        const sectionHeight = yPosition - categoryStartY;
        drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
      });
    } else {
      // Quick assessment - wrap in section border
      const quickSectionStartY = yPosition;
      reportData.items.forEach((item) => {
        if (yPosition + rowHeight > pageHeight - footerHeight) {
          // Draw section border before page break
          const sectionHeight = yPosition - quickSectionStartY;
          if (sectionHeight > 0) {
            drawSectionBorder(tableStartX, quickSectionStartY, sectionHeight, 0.8);
          }
          
          // Add footer to current page
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
          
          // Reset section start for new page
          const newQuickSectionStartY = yPosition;
        }

        drawTableDataRow(item, yPosition);
        yPosition += rowHeight;
      });
      
      // Draw section border for quick assessment
      const quickSectionHeight = yPosition - quickSectionStartY;
      if (quickSectionHeight > 0) {
        drawSectionBorder(tableStartX, quickSectionStartY, quickSectionHeight, 0.8);
      }
    }

    // Notes section
    if (yPosition + 30 > pageHeight - footerHeight) {
      // Add footer to current page
      addFooter(doc, pageWidth, pageHeight, footerHeight);
      doc.addPage();
      yPosition = headerHeight;
    } else {
      yPosition += 5;
    }
    
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', margin, yPosition);
    yPosition += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const notesText = reportData.notes && reportData.notes.trim() ? reportData.notes : '_________________________________________________________________\n_________________________________________________________________\n_________________________________________________________________';
    const notesLines = doc.splitTextToSize(notesText, pageWidth - 2 * margin);
    doc.text(notesLines, margin, yPosition);

    // Add footer to last page
    addFooter(doc, pageWidth, pageHeight, footerHeight);

    // Save PDF with sport name in filename
    const fileName = `${sportName.replace(/\s+/g, '_')}_${reportData.assessmentType === 'master-training' ? 'Master_Training' : 'High-level'}_Assessment_${playerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('PDF report generated successfully!');
  };

  const generateLevelCompletionCertificate = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    const playerName = `${player?.firstName || player?.user?.firstName || ''} ${player?.lastName || player?.user?.lastName || ''}`.trim() || 'Player';
    const academyName = player?.academyName || 'N/A';
    const evaluatedByLabel = selectedCoach === 'self'
      ? 'Self Evaluation'
      : selectedCoach === 'coach'
        ? 'Coach Evaluation'
      : (coachOptions.find((c) => c.id === selectedCoach)?.name || coachName || 'Coach');
    const levelLabel = (trainingLevel || 'beginner').toUpperCase();
    const totalScore = performanceItems.reduce((sum, item) => sum + (item.rating || 0), 0);
    const maxScore = performanceItems.length * 5;
    const completionDate = new Date().toLocaleDateString();

    doc.setFillColor(245, 247, 255);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    doc.setDrawColor(70, 99, 184);
    doc.setLineWidth(1.2);
    doc.rect(8, 8, pageWidth - 16, pageHeight - 16);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(33, 51, 120);
    doc.setFontSize(24);
    doc.text('Level Completion Certificate', pageWidth / 2, 30, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('EncycGames Training Module', pageWidth / 2, 39, { align: 'center' });

    let y = 58;
    const drawRow = (label: string, value: string) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(`${label}:`, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, margin + 45, y);
      y += 10;
    };

    drawRow('Player', playerName);
    drawRow('Academy', academyName);
    drawRow('Sport', selectedSport || 'Badminton');
    drawRow('Level', levelLabel);
    drawRow('Coach', evaluatedByLabel);
    drawRow('Completion Date', completionDate);
    drawRow('Score', `${totalScore} / ${maxScore}`);

    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(33, 51, 120);
    doc.text('Performance Summary', margin, y);
    y += 8;
    doc.setTextColor(50, 50, 50);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(
      `The player has completed ${levelLabel} training level and is evaluated with an overall score of ${totalScore}/${maxScore}.`,
      margin,
      y,
      { maxWidth: pageWidth - margin * 2 }
    );

    const signatureY = pageHeight - 40;
    doc.setDrawColor(90, 90, 90);
    doc.line(margin, signatureY, margin + 60, signatureY);
    doc.line(pageWidth - margin - 60, signatureY, pageWidth - margin, signatureY);
    doc.setFontSize(10);
    doc.text('Coach Signature', margin, signatureY + 6);
    doc.text('Academy Seal / Signature', pageWidth - margin - 60, signatureY + 6);

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('Generated by EncycGames', pageWidth / 2, pageHeight - 14, { align: 'center' });

    doc.save(`Level_Completion_Certificate_${playerName.replace(/\s+/g, '_')}_${levelLabel}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const generateManualAssessmentForm = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const footerHeight = 15;
    const headerHeight = 20;
    let yPosition = headerHeight;
    const rowHeight = 7;
    const tableStartX = margin;
    let tableStartY = margin;

    // Header with branding
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Encycdata', margin, 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('EncycGames', margin, 13);
    
    // Assessment title with sport name
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const sportName = selectedSport || 'Badminton';
    doc.text(
      `${sportName} - Manual ${assessmentType === 'master-training' ? 'Master Data Training' : 'High-level'} Assessment Form`,
      pageWidth / 2,
      yPosition,
      { align: 'center' }
    );
    yPosition += 8;

    // Header Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const playerName = `${player?.firstName || player?.user?.firstName || ''} ${player?.lastName || player?.user?.lastName || ''}`.trim();
    const academyName = player?.academyName || 'N/A';
    const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    doc.text(`Player: ${playerName}`, margin, yPosition);
    doc.text(`Coach: ${coachName || 'N/A'}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Academy: ${academyName}`, margin, yPosition);
    doc.text(`Date: ${reportDate}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Period: ${currentDateRange.start} to ${currentDateRange.end} (${getPeriodLabel(evaluationPeriod)} ${evaluationYear})`, margin, yPosition);
    yPosition += 8;

    // Rating Scale Reference Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Rating Scale Reference (For Information Only):', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Calculate the maximum width of level names for alignment
    const levelNames = [
      'Beginner (1-2):',
      'Intermediate (2-3):',
      'Advanced (3-4):',
      'Master (4-5):'
    ];
    doc.setFont('helvetica', 'bold');
    const maxLevelWidth = Math.max(...levelNames.map(name => doc.getTextWidth(name)));
    const definitionStartX = margin + 8 + maxLevelWidth + 5;
    
    // Beginner (1-2)
    doc.setFillColor(244, 67, 54);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Beginner (1-2):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Learning basic rules and shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Intermediate (2-3)
    doc.setFillColor(255, 152, 0);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Intermediate (2-3):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can perform upto 5 consistent strokes but lacking accuracy and perfection', definitionStartX, yPosition);
    yPosition += 5;
    
    // Advanced (3-4)
    doc.setFillColor(33, 150, 243);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Advanced (3-4):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can play 15 or more consistent strokes and do long rallies with different shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Master (4-5)
    doc.setFillColor(76, 175, 80);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Master (4-5):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Aggressive, Accurate and consistently performing in varying conditions', definitionStartX, yPosition);
    yPosition += 8;

    // Table column widths - reduced skill column, increased performance column
    const colWidths = {
      skill: (pageWidth - 2 * margin) * 0.38, // Reduced from 0.45
      notApplicable: (pageWidth - 2 * margin) * 0.10,
      rating: (pageWidth - 2 * margin) * 0.18,
      performance: (pageWidth - 2 * margin) * 0.27, // Increased from 0.20 to fit "Needs Improvement"
      smiley: (pageWidth - 2 * margin) * 0.07
    };
    
    const drawTableRow = (x: number, y: number, lineWidth: number = 0.2) => {
      doc.setLineWidth(lineWidth);
      let currentX = x;
      const lineY = y + rowHeight;
      const tableEndX = x + (pageWidth - 2 * margin);
      
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.skill;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.notApplicable;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.rating;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.performance;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.smiley;
      doc.line(currentX, y, currentX, lineY);
      
      doc.line(x, lineY, tableEndX, lineY);
    };

    const drawSectionBorder = (x: number, y: number, height: number, lineWidth: number = 0.8) => {
      doc.setLineWidth(lineWidth);
      const endX = x + (pageWidth - 2 * margin);
      const endY = y + height;
      
      doc.line(x, y, endX, y);
      doc.line(endX, y, endX, endY);
      doc.line(endX, endY, x, endY);
      doc.line(x, endY, x, y);
    };

    const drawTableHeader = (y: number) => {
      const headerY = y;
      
      doc.setFillColor(245, 245, 245);
      doc.rect(tableStartX, headerY, pageWidth - 2 * margin, rowHeight, 'F');
      
      doc.setLineWidth(0.5);
      doc.line(tableStartX, headerY, tableStartX + (pageWidth - 2 * margin), headerY);
      
      drawTableRow(tableStartX, headerY, 0.3);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let textX = tableStartX;
      doc.text('Skill', textX + colWidths.skill / 2, headerY + 5, { align: 'center' });
      textX += colWidths.skill;
      doc.text('N/A', textX + colWidths.notApplicable / 2, headerY + 5, { align: 'center' });
      textX += colWidths.notApplicable;
      doc.text('Rating', textX + colWidths.rating / 2, headerY + 5, { align: 'center' });
      textX += colWidths.rating;
      doc.text('Performance', textX + colWidths.performance / 2, headerY + 5, { align: 'center' });
      textX += colWidths.performance;
      doc.text('Smiley', textX + colWidths.smiley / 2, headerY + 5, { align: 'center' });
    };

    const drawTableDataRow = (item: BasicPerformanceItem, y: number, categoryColor?: { bg: number[], text: number[] }) => {
      const rowY = y;
      
      drawTableRow(tableStartX, rowY, 0.2);
      
      if (categoryColor) {
        doc.setFillColor(categoryColor.bg[0], categoryColor.bg[1], categoryColor.bg[2]);
        doc.rect(tableStartX, rowY, pageWidth - 2 * margin, rowHeight, 'F');
        drawTableRow(tableStartX, rowY, 0.2);
      }
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      let textX = tableStartX + 2;
      
      const skillName = doc.splitTextToSize(item.name, colWidths.skill - 4);
      doc.text(skillName, textX, rowY + 5);
      
      // Not Applicable checkbox (empty box for manual filling)
      textX += colWidths.skill + 2;
      doc.rect(textX, rowY + 2, 3, 3);
      
      // Rating (empty boxes for manual filling - no stars filled)
      textX += colWidths.notApplicable + 2;
      for (let i = 0; i < 5; i++) {
        doc.rect(textX + i * 4, rowY + 2, 3, 3);
      }
      
      // Performance level (blank for manual filling)
      textX += colWidths.rating + 2;
      // Leave blank for manual filling
      
      // Smiley (blank for manual filling)
      textX += colWidths.performance + 2;
      // Leave blank for manual filling
    };

    const addFooter = (doc: jsPDF, pageWidth: number, pageHeight: number, footerHeight: number) => {
      const footerY = pageHeight - footerHeight;
      doc.setLineWidth(0.5);
      doc.line(10, footerY, pageWidth - 10, footerY);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Page ${doc.getCurrentPageInfo().pageNumber}`,
        pageWidth / 2,
        footerY + 10,
        { align: 'center' }
      );
    };

    // Start table
    tableStartY = yPosition;
    drawTableHeader(tableStartY);
    yPosition += rowHeight;

    // Get predefined items for the form - use database items only
    const predefinedItems = assessmentItems.map(item => ({ name: item.name, category: item.category }));
    
    // If no items, return early
    if (predefinedItems.length === 0) {
      toast.error(`No assessment skills defined for ${selectedSport}. Cannot generate form.`);
      return;
    }

    if (assessmentType === 'master-training') {
      const groupedItems = predefinedItems.reduce((acc, item, index) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push({
          id: `skill-${index}`,
          itemCode: item.itemCode || `skill-${index}`,
          name: item.name,
          category: item.category,
          rating: 0,
          notes: '',
          evaluationPeriod: evaluationPeriod,
          coachId: coachId || '',
          playerId: player?.id || '',
          notApplicable: false
        });
        return acc;
      }, {} as { [key: string]: BasicPerformanceItem[] });

      Object.entries(groupedItems).forEach(([category, items]) => {
        if (yPosition + rowHeight * 2 > pageHeight - footerHeight) {
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
        }

        yPosition += 2;
        const categoryStartY = yPosition;
        const categoryColor = getCategoryColor(category);
        const bgColor = hexToRgb(categoryColor.bg);
        const textColor = hexToRgb(categoryColor.text);
        
        doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
        
        doc.setLineWidth(0.5);
        doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
        doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        yPosition += rowHeight;

        items.forEach((item) => {
          if (yPosition + rowHeight > pageHeight - footerHeight) {
            const sectionHeight = yPosition - categoryStartY;
            drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
            
            addFooter(doc, pageWidth, pageHeight, footerHeight);
            doc.addPage();
            yPosition = headerHeight;
            tableStartY = yPosition;
            drawTableHeader(tableStartY);
            yPosition += rowHeight;
            
            const newCategoryStartY = yPosition;
            yPosition += 2;
            doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
            doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
            doc.setLineWidth(0.5);
            doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
            doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(textColor[0], textColor[1], textColor[2]);
            doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
            doc.setTextColor(0, 0, 0);
            yPosition += rowHeight;
          }

          drawTableDataRow(item, yPosition, { bg: bgColor, text: textColor });
          yPosition += rowHeight;
        });
        
        const sectionHeight = yPosition - categoryStartY;
        drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
      });
    } else {
      const quickSectionStartY = yPosition;
      predefinedItems.forEach((item, index) => {
        if (yPosition + rowHeight > pageHeight - footerHeight) {
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
          
          const newQuickSectionStartY = yPosition;
        }

        const formItem: BasicPerformanceItem = {
          id: `skill-${index}`,
          itemCode: item.itemCode || `skill-${index}`,
          name: item.name,
          category: item.category,
          rating: 0,
          notes: '',
          evaluationPeriod: evaluationPeriod,
          coachId: coachId || '',
          playerId: player?.id || '',
          notApplicable: false
        };
        drawTableDataRow(formItem, yPosition);
        yPosition += rowHeight;
      });
      
      const quickSectionHeight = yPosition - quickSectionStartY;
      if (quickSectionHeight > 0) {
        drawSectionBorder(tableStartX, quickSectionStartY, quickSectionHeight, 0.8);
      }
    }

    // Notes section
    if (yPosition + 30 > pageHeight - footerHeight) {
      addFooter(doc, pageWidth, pageHeight, footerHeight);
      doc.addPage();
      yPosition = headerHeight;
    } else {
      yPosition += 5;
    }
    
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', margin, yPosition);
    yPosition += 8;

    // Notes section without lines - just blank space
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    // Leave blank space for notes (no underline lines)
    yPosition += 20; // Add some space for notes

    addFooter(doc, pageWidth, pageHeight, footerHeight);

    // Save PDF with EncycGames prefix and sport name
    const fileName = `EncycGames_${sportName.replace(/\s+/g, '_')}_Manual_${assessmentType === 'master-training' ? 'Master_Training' : 'High-level'}_Assessment_Form_${playerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('Manual Assessment Form generated successfully!');
  };

  const totalScoreSummary = performanceItems.reduce((sum, item) => sum + (item.rating || 0), 0);
  const maxScoreSummary = performanceItems.length * 5;
  const inferredFeedback = useMemo(() => {
    if (lastSaveSummary || performanceItems.length === 0) return null;

    let increased = 0;
    let decreased = 0;
    let same = 0;
    let previousTotalScore = 0;
    let currentTotalScore = 0;
    let maxTotalScore = 0;
    let latestAtMs: number | null = null;
    let previousAtMs: number | null = null;
    const inferredChanges: RatingChangeItem[] = [];

    performanceItems.forEach((item) => {
      if (item.notApplicable) return;
      const code = item.itemCode || item.id;
      const points = skillProgressByCode[code] || [];
      if (points.length === 0) return;

      const currentPoint = points[points.length - 1];
      const previousPoint = points.length > 1 ? points[points.length - 2] : null;
      const current = Number(currentPoint?.rating || 0);
      const previous = Number(previousPoint?.rating ?? current);

      maxTotalScore += 5;
      if (previous > 0 || current > 0) {
        currentTotalScore += current;
        previousTotalScore += previous;
      }

      if (current > previous) increased++;
      else if (current < previous) decreased++;
      else same++;

      if (current !== previous) {
        inferredChanges.push({
          itemKey: code,
          itemName: item.name,
          previous,
          current,
          direction: current > previous ? 'up' : 'down',
        });
      }

      const currentAtMs = new Date(currentPoint.evaluatedAt).getTime();
      latestAtMs = latestAtMs === null ? currentAtMs : Math.max(latestAtMs, currentAtMs);
      if (previousPoint?.evaluatedAt) {
        const prevAtMs = new Date(previousPoint.evaluatedAt).getTime();
        previousAtMs = previousAtMs === null ? prevAtMs : Math.max(previousAtMs, prevAtMs);
      }
    });

    if (maxTotalScore === 0) return null;

    const improvementRatePct = previousTotalScore > 0
      ? ((currentTotalScore - previousTotalScore) / previousTotalScore) * 100
      : (currentTotalScore > 0 ? 100 : 0);
    const totalScorePct = maxTotalScore > 0 ? (currentTotalScore / maxTotalScore) * 100 : 0;
    const daysSincePreviousSave =
      latestAtMs !== null && previousAtMs !== null
        ? Math.max(0, Math.round((latestAtMs - previousAtMs) / (1000 * 60 * 60 * 24)))
        : null;

    return {
      summary: {
        increased,
        decreased,
        same,
        daysSincePreviousSave,
        currentSavedAt: new Date(latestAtMs ?? Date.now()).toISOString(),
        previousTotalScore,
        currentTotalScore,
        maxTotalScore,
        improvementRatePct,
        totalScorePct,
      } as LastSaveSummary,
      changes: inferredChanges,
    };
  }, [lastSaveSummary, performanceItems, skillProgressByCode]);

  const feedbackSummary = lastSaveSummary || inferredFeedback?.summary || null;
  const feedbackChanges = lastSaveSummary ? lastSaveChangedItems : (inferredFeedback?.changes || []);
  const improvedSkillsForPanel = feedbackChanges.filter((change) => change.direction === 'up');
  const totalScoreTrendData = useMemo(() => {
    const history = previousEvaluations
      .filter((evaluation) => Array.isArray(evaluation.items) && evaluation.items.length > 0)
      .map((evaluation) => {
        const validItems = evaluation.items.filter((item) => !item.notApplicable);
        const score = validItems.reduce((sum, item) => sum + Number(item.rating || 0), 0);
        const max = validItems.length * 5;
        return {
          at: evaluation.submittedAt,
          label: new Date(evaluation.submittedAt).toLocaleDateString(),
          score,
          scorePct: max > 0 ? (score / max) * 100 : 0,
        };
      })
      .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

    if (feedbackSummary) {
      const latest = history[history.length - 1];
      const shouldAppendCurrent =
        !latest ||
        latest.at !== feedbackSummary.currentSavedAt ||
        latest.score !== feedbackSummary.currentTotalScore;
      if (shouldAppendCurrent) {
        history.push({
          at: feedbackSummary.currentSavedAt,
          label: 'Current',
          score: feedbackSummary.currentTotalScore,
          scorePct: feedbackSummary.totalScorePct,
        });
      }
    }

    return history.slice(-12);
  }, [previousEvaluations, feedbackSummary]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0, mt: 0 }}>
      {/* Title with Period */}
      <Box sx={{ mb: 2, mt: 0, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(() => {
              const getSportIcon = (sport: string) => {
                switch (sport?.toLowerCase()) {
                  case 'badminton':
                    return <BadmintonIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
                  case 'tennis':
                    return <TennisIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
                  case 'basketball':
                    return <BasketballIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
                  case 'football':
                    return <FootballIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
                  case 'volleyball':
                    return <VolleyballIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
                  case 'cricket':
                    return <CricketIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
                  default:
                    return <SportsIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
                }
              };
              return getSportIcon(selectedSport);
            })()}
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                fontSize: '1.5rem'
              }}
            >
              {selectedSport}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {assessmentType === 'master-training'
                ? `Skill Assessment (${trainingLevel ? trainingLevel.toUpperCase() : 'ALL LEVELS'})`
                : `Skill Assessment High-level - ${currentDateRange.start} to ${currentDateRange.end} (${getPeriodLabel(evaluationPeriod)} ${evaluationYear})`}
            </Typography>
            <Chip 
              label={assessmentType === 'master-training' ? (isLevelCompleted ? 'Level Completed' : 'In Progress') : (isSubmitted ? 'Submitted' : 'Pending')}
              color={assessmentType === 'master-training' ? (isLevelCompleted ? 'success' : 'info') : (isSubmitted ? 'success' : 'warning')}
              size="small"
              sx={{ height: 24, fontSize: '0.75rem' }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* Second Line: Dropdowns and Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Evaluation mode is now chosen from sidebar route hierarchy */}
          {false && onCoachChange && (
            <TextField
              select
              label="Evaluated By"
              value={selectedCoach || 'self'}
              onChange={(e) => {
                if (onCoachChange) {
                  onCoachChange(e.target.value);
                }
              }}
              size="small"
              sx={{ minWidth: 250 }}
              SelectProps={{ native: true }}
            >
              {coachOptions.map((coach) => (
                <option key={coach.id} value={coach.id}>
                  👨‍🏫 Coach: {coach.name}
                </option>
              ))}
              <option value="self">
                👤 Self-Evaluation
              </option>
            </TextField>
          )}
          <Chip
            label={`Training Score: ${totalScoreSummary} / ${maxScoreSummary}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 700, height: 34 }}
          />
          {assessmentType !== 'master-training' && (
            <>
          <TextField
            select
            label="Evaluation Year"
            value={evaluationYear}
            onChange={(e) => {
              const year = parseInt(e.target.value);
              setEvaluationYear(year);
              setSelectedEvaluation('current');
            }}
            size="small"
            sx={{ minWidth: 120 }}
            SelectProps={{ native: true }}
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = 2025 + i;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </TextField>
          <TextField
            select
            label="Evaluation Period"
            value={evaluationPeriod}
            onChange={(e) => {
              const newPeriod = e.target.value;
              setEvaluationPeriod(newPeriod);
              setSelectedEvaluation('current');
            }}
            size="small"
            sx={{ minWidth: 200 }}
            SelectProps={{ native: true }}
          >
            <optgroup label="Year">
              <option value="yearly">Yearly (Jan-Dec)</option>
            </optgroup>
            <optgroup label="Quarters">
              <option value="quarter-1">Q1 (Jan-Mar)</option>
              <option value="quarter-2">Q2 (Apr-Jun)</option>
              <option value="quarter-3">Q3 (Jul-Sep)</option>
              <option value="quarter-4">Q4 (Oct-Dec)</option>
            </optgroup>
          </TextField>
            </>
          )}
          
          {/* Show save/submit/download buttons for self-evaluation (players) or coach evaluations */}
          {/* Also show download button for players viewing submitted coach evaluations */}
          {(isSelfEvaluation || canCoachEdit || (isPlayer && !isSelfEvaluation && isSubmitted)) && (
            <>
              {/* Show buttons - SIMPLE pattern like RankingEvaluationWithTabs */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Only show Save/Submit buttons if user can edit (self-evaluation or coach editing) */}
                {(isSelfEvaluation || canCoachEdit) && (
                  <>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSave();
                      }}
                      disabled={selectedEvaluation !== 'current' || saving || loading || performanceItems.length === 0 || !canEdit || isSubmitted || (assessmentType === 'master-training' && canCoachEdit && isLevelCompleted)}
                      sx={{ minWidth: 120 }}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    {assessmentType !== 'master-training' && (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit();
                      }}
                      sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' }, minWidth: 120 }}
                      disabled={selectedEvaluation !== 'current' || saving || loading || performanceItems.length === 0 || !canEdit || isSubmitted}
                    >
                      {loading ? 'Submitting...' : 'Submit'}
                    </Button>
                    )}
                  </>
                )}
                {/* Show Download Report if isSubmitted is true - for both self-evaluations and coach-submitted evaluations */}
                {(isSubmitted || assessmentType === 'master-training') && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PdfIcon />}
                    onClick={generatePDFReport}
                    sx={{ minWidth: 160 }}
                  >
                    Download Report
                  </Button>
                )}
                {assessmentType === 'master-training' && canCoachEdit && (
                  <Button
                    variant={isLevelCompleted ? 'outlined' : 'contained'}
                    color={isLevelCompleted ? 'success' : 'warning'}
                    onClick={handleMarkLevelComplete}
                    disabled={isLevelCompleted || lockingLevel}
                    sx={{ minWidth: 170 }}
                  >
                    {isLevelCompleted ? 'Level Completed' : (lockingLevel ? 'Locking...' : 'Level Complete')}
                  </Button>
                )}
                {assessmentType === 'master-training' && isLevelCompleted && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PdfIcon />}
                    onClick={generateLevelCompletionCertificate}
                    sx={{ minWidth: 220 }}
                  >
                    Level Completion Certificate
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Rating Scale Reference - For Information Only */}
      {/* Hide for high-level and low-level assessments, show for drill assessment with new format */}
      {assessmentType === 'drill' && (
        <Paper 
          sx={{ 
            mb: 2, 
            p: 1.5, 
            bgcolor: '#f5f5f5',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.8rem', color: '#666' }}>
            Rating Scale Reference (For Information Only):
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Chip label="Rating 1:" size="small" sx={{ bgcolor: '#f44336', color: 'white', fontWeight: 700, minWidth: 70 }} />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                Inconsistent contact with the shuttle and ready stance
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Chip label="Rating 2:" size="small" sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 700, minWidth: 70 }} />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                Hit 1-3 shots with proper grip and action but inefficient footwork
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Chip label="Rating 3:" size="small" sx={{ bgcolor: '#ffc107', color: 'white', fontWeight: 700, minWidth: 70 }} />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                Hit 4-10 shots rally with basic footwork, proper grip and control
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Chip label="Rating 4:" size="small" sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 700, minWidth: 70 }} />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                Hit 10+ shot rallies consistently with proper footwork and accuracy and pace
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Chip label="Rating 5:" size="small" sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 700, minWidth: 70 }} />
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1 }}>
                15+ shot rallies with rapid footwork, high speed, accuracy, and tactical placement
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Navigation Index for Advanced Assessment (Low-level, Fitness, Drill) */}
      {(assessmentType === 'master-training' || assessmentType === 'fitness' || assessmentType === 'drill') && (() => {
        const groupedItems = performanceItems.reduce((acc, item) => {
          if (!acc[item.category]) {
            acc[item.category] = [];
          }
          acc[item.category].push(item);
          return acc;
        }, {} as Record<string, BasicPerformanceItem[]>);
        
        const categories = Object.keys(groupedItems);
        if (categories.length > 0) {
          const scrollToSection = (category: string) => {
            const element = document.getElementById(`section-${category}`);
            if (element) {
              // Calculate offset to account for sticky headers/navigation
              const offset = 100; // Adjust this value based on your header height
              const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementPosition - offset;
              
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
            }
          };

          const scrollToNext = () => {
            const currentScroll = window.pageYOffset;
            let currentIndex = -1;
            
            // Find which section we're currently viewing
            categories.forEach((category, index) => {
              const element = document.getElementById(`section-${category}`);
              if (element) {
                const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
                const elementBottom = elementTop + element.offsetHeight;
                // Check if current scroll position is within this section
                if (currentScroll + 200 >= elementTop && currentScroll <= elementBottom) {
                  currentIndex = index;
                }
              }
            });
            
            // If we found the current section, go to next one
            if (currentIndex >= 0 && currentIndex < categories.length - 1) {
              scrollToSection(categories[currentIndex + 1]);
            } else if (currentIndex === categories.length - 1) {
              // Already at last section, scroll to bottom of page
              window.scrollTo({
                top: document.documentElement.scrollHeight - window.innerHeight,
                behavior: 'smooth'
              });
            } else {
              // Find the first section below current scroll
              let nextSection: string | null = null;
              let minDistance = Infinity;
              
              categories.forEach((category) => {
                const element = document.getElementById(`section-${category}`);
                if (element) {
                  const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
                  const distance = elementTop - currentScroll;
                  if (distance > 0 && distance < minDistance) {
                    minDistance = distance;
                    nextSection = category;
                  }
                }
              });
              
              if (nextSection) {
                scrollToSection(nextSection);
              } else {
                // No section below, scroll to bottom
                window.scrollTo({
                  top: document.documentElement.scrollHeight - window.innerHeight,
                  behavior: 'smooth'
                });
              }
            }
          };

          const scrollToPrevious = () => {
            const currentScroll = window.pageYOffset;
            let currentIndex = -1;
            const viewportTop = currentScroll;
            const viewportBottom = currentScroll + window.innerHeight;
            
            // Find which section is currently most visible in viewport
            categories.forEach((category, index) => {
              const element = document.getElementById(`section-${category}`);
              if (element) {
                const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
                const elementBottom = elementTop + element.offsetHeight;
                
                // Check if section is visible in viewport (at least partially)
                if (elementTop <= viewportBottom && elementBottom >= viewportTop) {
                  // If this section is more visible than previous, update currentIndex
                  const visibleTop = Math.max(elementTop, viewportTop);
                  const visibleBottom = Math.min(elementBottom, viewportBottom);
                  const visibleHeight = visibleBottom - visibleTop;
                  
                  if (currentIndex === -1 || visibleHeight > 0) {
                    currentIndex = index;
                  }
                }
              }
            });
            
            // If we found the current section, go to previous one
            if (currentIndex > 0) {
              // Go to previous section
              scrollToSection(categories[currentIndex - 1]);
            } else if (currentIndex === 0) {
              // Already at first section, scroll to top of page
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            } else {
              // Couldn't find current section, find the last section above current scroll
              let prevSection: string | null = null;
              let maxDistance = -Infinity;
              
              categories.forEach((category) => {
                const element = document.getElementById(`section-${category}`);
                if (element) {
                  const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
                  const distance = currentScroll - elementTop;
                  // Section is above current scroll position
                  if (distance > 0 && distance > maxDistance) {
                    maxDistance = distance;
                    prevSection = category;
                  }
                }
              });
              
              if (prevSection) {
                scrollToSection(prevSection);
              } else {
                // No section above, scroll to top of page
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }
            }
          };

          return (
            <>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>Skill Category</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {categories.map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      onClick={() => scrollToSection(category)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: getCategoryColor(category).bg,
                        color: getCategoryColor(category).text,
                        '&:hover': {
                          bgcolor: getCategoryColor(category).text,
                          color: getCategoryColor(category).bg,
                        }
                      }}
                    />
                  ))}
                </Box>
              </Paper>
              {/* Floating Navigation Buttons */}
              <Box
                sx={{
                  position: 'fixed',
                  right: 24,
                  bottom: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  zIndex: 1000,
                }}
              >
                <Tooltip title="Previous Section" placement="left">
                  <Fab
                    size="small"
                    color="primary"
                    onClick={scrollToPrevious}
                    sx={{
                      boxShadow: 3,
                    }}
                  >
                    <ArrowUpIcon />
                  </Fab>
                </Tooltip>
                <Tooltip title="Next Section" placement="left">
                  <Fab
                    size="small"
                    color="primary"
                    onClick={scrollToNext}
                    sx={{
                      boxShadow: 3,
                    }}
                  >
                    <ArrowDownIcon />
                  </Fab>
                </Tooltip>
              </Box>
            </>
          );
        }
        return null;
      })()}

      {/* Bulk Rating Controls and Filter */}
      {/* Allow bulk rating for both players (self-evaluation) and coaches */}
      {false && (assessmentType === 'master-training' || !isSubmitted) && selectedEvaluation === 'current' && canEdit && (() => {
        // Calculate rated and not-rated counts
        const ratedCount = performanceItems.filter(item => item.rating > 0).length;
        const notRatedCount = performanceItems.filter(item => item.rating === 0).length;
        
        return (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Bulk Rating:
              </Typography>
              <Rating
                value={bulkRating}
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    setBulkRating(newValue);
                  }
                }}
                size="small"
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyBulkRating}
                disabled={bulkRating === 0 || selectedItems.size === 0}
                sx={{ bgcolor: '#1976d2' }}
              >
                Apply to {selectedItems.size} Selected
              </Button>
              {selectedItems.size > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedItems(new Set())}
                >
                  Clear Selection
                </Button>
              )}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Rating Status</InputLabel>
                  <Select
                    value={filterRatedStatus}
                    label="Filter by Rating Status"
                    onChange={(e) => setFilterRatedStatus(e.target.value)}
                  >
                    <MenuItem value="all">All Skills</MenuItem>
                    <MenuItem value="rated">Rated Only</MenuItem>
                    <MenuItem value="not-rated">Not Rated Only</MenuItem>
                    <MenuItem value="1">Rating: 1 Star</MenuItem>
                    <MenuItem value="2">Rating: 2 Stars</MenuItem>
                    <MenuItem value="3">Rating: 3 Stars</MenuItem>
                    <MenuItem value="4">Rating: 4 Stars</MenuItem>
                    <MenuItem value="5">Rating: 5 Stars</MenuItem>
                  </Select>
                </FormControl>
                <Chip 
                  label={`Rated: ${ratedCount}`} 
                  color="success" 
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
                <Chip 
                  label={`Not Rated: ${notRatedCount}`} 
                  color="default" 
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Box>
        );
      })()}

      <Box sx={{ mb: 2 }}>
        {assessmentType !== 'master-training' && (
          <Card
            sx={{
              mb: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 1.5,
              border: '1px solid #90caf9',
              bgcolor: '#e3f2fd',
              boxShadow: 'none',
            }}
          >
            <Typography variant="caption" sx={{ color: '#0d47a1', fontWeight: 700, lineHeight: 1.3 }}>
              Skill | Not Applicable | Rating | Performance | Area of Improvement | Suggested Practices
            </Typography>
          </Card>
        )}

        {(() => {
          if (loadingItems) {
            return (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  Loading assessment skills...
                </Typography>
              </Paper>
            );
          }

          if (!loadingItems && performanceItems.length === 0 && assessmentItems.length === 0) {
            return (
              <Alert severity="info" sx={{ maxWidth: 700 }}>
                No assessment skills defined for {selectedSport}. Please contact your administrator.
              </Alert>
            );
          }

          if (performanceItems.length === 0 && assessmentItems.length > 0) {
            return (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  Initializing assessment form...
                </Typography>
              </Paper>
            );
          }

          let filteredItems = performanceItems;
          if (filterRatedStatus === 'rated') {
            filteredItems = performanceItems.filter(item => item.rating > 0);
          } else if (filterRatedStatus === 'not-rated') {
            filteredItems = performanceItems.filter(item => item.rating === 0);
          } else if (['1', '2', '3', '4', '5'].includes(filterRatedStatus)) {
            const ratingValue = parseInt(filterRatedStatus);
            filteredItems = performanceItems.filter(item => item.rating === ratingValue);
          }

          if (filteredItems.length === 0) {
            return (
              <Alert severity="info">
                No skills match the selected rating filter.
              </Alert>
            );
          }

          const renderItemRow = (item: BasicPerformanceItem, borderColor?: string) => {
            const performance = getPerformanceLevel(item.rating);
            const disableEdit = ((assessmentType === 'master-training' && canCoachEdit && isLevelCompleted) ? true : (assessmentType === 'master-training' ? false : isSubmitted)) || selectedEvaluation !== 'current' || !canEdit;
            const itemCode = item.itemCode || item.id;
            const trendDirection = getTrendDirectionForItem(item);
            const progressPoints = skillProgressByCode[itemCode] || [];
            const progressChartData = progressPoints.map((point) => ({
              label: new Date(point.evaluatedAt).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }),
              rating: point.rating,
              at: new Date(point.evaluatedAt).toLocaleDateString(),
            }));
            const sparklineData =
              progressChartData.length === 1
                ? [
                    { ...progressChartData[0], label: 'Start' },
                    { ...progressChartData[0], label: 'Now' },
                  ]
                : progressChartData;

            if (assessmentType === 'master-training' || assessmentType === 'fitness' || assessmentType === 'drill') {
              return (
                <Accordion
                  key={item.id}
                  expanded={expandedSkillCard === item.id}
                  onChange={(_, expanded) => {
                    setExpandedSkillCard(expanded ? item.id : null);
                    if (expanded) {
                      void loadSkillProgress(itemCode);
                    }
                  }}
                  sx={{
                    mb: 0.75,
                    border: '1px solid #dbe8f6',
                    borderLeft: `4px solid ${borderColor || '#90caf9'}`,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                      {(assessmentType === 'master-training' || !isSubmitted) && selectedEvaluation === 'current' && canEdit && (
                        <Checkbox
                          checked={selectedItems.has(item.id)}
                          onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                          disabled={disableEdit}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                      )}
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                        {getDisplayItemName(item.name)}
                      </Typography>
                      <Rating
                        value={item.rating}
                        onChange={(_, newValue) => {
                          if (newValue !== null) {
                            handleRatingChange(item.id, newValue);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        disabled={disableEdit}
                      />
                      {trendDirection === 'up' ? (
                        <ArrowUpIcon sx={{ color: 'success.main', fontSize: 18 }} />
                      ) : trendDirection === 'down' ? (
                        <ArrowDownIcon sx={{ color: 'error.main', fontSize: 18 }} />
                      ) : (
                        <Box sx={{ width: 18 }} />
                      )}
                      <Box sx={{ minWidth: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {getSmileyIcon(item.rating)}
                      </Box>
                      <Box sx={{ width: 120, height: 28 }}>
                        {sparklineData.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={sparklineData}>
                              <Line type="monotone" dataKey="rating" stroke="#1976d2" strokeWidth={2} dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            Trend pending
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {getSmileyIcon(item.rating)}
                        </Box>
                      </Box>

                      <FormControl size="small" disabled={disableEdit} sx={{ maxWidth: 260 }}>
                        <InputLabel>Area of Improvement</InputLabel>
              <Select
                          multiple
                          label="Area of Improvement"
                          value={item.areaOfImprovement && item.areaOfImprovement.length > 0 ? item.areaOfImprovement : [defaultGuidanceOption]}
                          onChange={(e) => handleAreaOfImprovementChange(item.id, e.target.value as string[])}
                          renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                          {normalizeGuidanceOptions(item.areaOfImprovementOptions).map((option) => (
                            <MenuItem key={`${item.id}-improve-${option}`} value={option}>
                              <Checkbox size="small" checked={(item.areaOfImprovement || [defaultGuidanceOption]).includes(option)} />
                              <Typography variant="caption">{option}</Typography>
                            </MenuItem>
                          ))}
              </Select>
            </FormControl>

                      <FormControl size="small" disabled={disableEdit} sx={{ maxWidth: 260 }}>
                        <InputLabel>Suggested Practices</InputLabel>
                        <Select
                          multiple
                          label="Suggested Practices"
                          value={item.suggestedPractices && item.suggestedPractices.length > 0 ? item.suggestedPractices : [defaultGuidanceOption]}
                          onChange={(e) => handleSuggestedPracticesChange(item.id, e.target.value as string[])}
                          renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                          {normalizeGuidanceOptions(item.suggestedPracticesOptions).map((option) => (
                            <MenuItem key={`${item.id}-suggest-${option}`} value={option}>
                              <Checkbox size="small" checked={(item.suggestedPractices || [defaultGuidanceOption]).includes(option)} />
                              <Typography variant="caption">{option}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {sparklineData.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                            Saved Rating History ({progressChartData.length} entries)
                          </Typography>
                          <Box sx={{ width: '100%', height: 160 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={sparklineData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis domain={[0, 5]} allowDecimals={false} />
                                <RechartsTooltip />
                                <Line type="monotone" dataKey="rating" stroke="#1976d2" strokeWidth={2} dot />
                              </LineChart>
                            </ResponsiveContainer>
          </Box>
          </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            }

            return (
              <Card
                key={item.id}
                sx={{
                  mb: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.25,
                  border: '1px solid #dbe8f6',
                  borderLeft: `4px solid ${borderColor || '#90caf9'}`,
                  boxShadow: 'none',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                    width: '100%',
                    overflowX: 'auto',
                  }}
                >
                  {!isSubmitted && selectedEvaluation === 'current' && canEdit ? (
                    <Checkbox
                      checked={selectedItems.has(item.id)}
                      onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                      disabled={disableEdit || item.notApplicable}
                      size="small"
                      sx={{ p: 0.5, flexShrink: 0 }}
                    />
                  ) : (
                    <Box sx={{ width: 28, flexShrink: 0 }} />
                  )}

                  <Typography
                    variant="caption"
                    sx={{ color: '#263238', fontWeight: 700, minWidth: 180, flex: 1, whiteSpace: 'nowrap' }}
                  >
                    {getDisplayItemName(item.name)}
                  </Typography>

                  <FormControlLabel
                    sx={{ m: 0, flexShrink: 0 }}
                    control={
                      <Checkbox
                        checked={item.notApplicable || false}
                        onChange={(e) => handleNotApplicableChange(item.id, e.target.checked)}
                        disabled={disableEdit}
                        size="small"
                      />
                    }
                    label={<Typography variant="caption">N/A</Typography>}
                  />

                  <Rating
                    value={item.rating}
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        handleRatingChange(item.id, newValue);
                      }
                    }}
                    size="small"
                    disabled={disableEdit || item.notApplicable}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: performance.color,
                      fontWeight: 600,
                      minWidth: 120,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.notApplicable ? 'Not Applicable' : performance.text}
                  </Typography>

                  <Box sx={{ minWidth: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {item.notApplicable ? (
                      <Typography component="span" variant="caption" color="text.secondary">N/A</Typography>
                    ) : (
                      getSmileyIcon(item.rating)
                    )}
                  </Box>

                  <FormControl
                    size="small"
                    sx={{ minWidth: 190, flexShrink: 0 }}
                    disabled={disableEdit}
                  >
                    <Select
                      multiple
                      displayEmpty
                      value={item.areaOfImprovement && item.areaOfImprovement.length > 0 ? item.areaOfImprovement : [defaultGuidanceOption]}
                      onChange={(e) => handleAreaOfImprovementChange(item.id, e.target.value as string[])}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      {normalizeGuidanceOptions(item.areaOfImprovementOptions).map((option) => (
                        <MenuItem key={`${item.id}-improve-${option}`} value={option}>
                          <Checkbox size="small" checked={(item.areaOfImprovement || [defaultGuidanceOption]).includes(option)} />
                          <Typography variant="caption">{option}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{ minWidth: 210, flexShrink: 0 }}
                    disabled={disableEdit}
                  >
                    <Select
                      multiple
                      displayEmpty
                      value={item.suggestedPractices && item.suggestedPractices.length > 0 ? item.suggestedPractices : [defaultGuidanceOption]}
                      onChange={(e) => handleSuggestedPracticesChange(item.id, e.target.value as string[])}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      {normalizeGuidanceOptions(item.suggestedPracticesOptions).map((option) => (
                        <MenuItem key={`${item.id}-suggest-${option}`} value={option}>
                          <Checkbox size="small" checked={(item.suggestedPractices || [defaultGuidanceOption]).includes(option)} />
                          <Typography variant="caption">{option}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Card>
            );
          };

          if (assessmentType === 'master-training' || assessmentType === 'fitness' || assessmentType === 'drill') {
            const groupedItems = filteredItems.reduce((acc, item) => {
              if (!acc[item.category]) {
                acc[item.category] = [];
              }
              acc[item.category].push(item);
              return acc;
            }, {} as Record<string, BasicPerformanceItem[]>);

            const groupedSections = Object.entries(groupedItems).map(([category, items]) => {
              const ratedItems = items.filter(item => !item.notApplicable && item.rating > 0);
              const averageScore = ratedItems.length > 0
                ? (ratedItems.reduce((sum, item) => sum + item.rating, 0) / ratedItems.length).toFixed(2)
                : 'N/A';
              const categoryColor = getCategoryColor(category);
              return (
                <Box key={category} sx={{ mb: 1.25 }}>
                  <Card
                    id={`section-${category}`}
                    sx={{
                      scrollMarginTop: '100px',
                      mb: 0.75,
                      px: 1.25,
                      py: 0.6,
                      borderRadius: 1.25,
                      border: `1px solid ${categoryColor.text}`,
                      bgcolor: categoryColor.bg,
                      boxShadow: 'none',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: categoryColor.text }}>
                      {category}
                    </Typography>
                  </Card>
                  {items.map(item => renderItemRow(item, categoryColor.text))}
                  <Paper sx={{ mt: 0.5, p: 1, bgcolor: '#fafafa', border: '1px solid #eee' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Average Score: {averageScore}
                    </Typography>
                  </Paper>
                  <Box sx={{ mt: 0.75 }}>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      label="Notes/Comments"
                      value={sectionNotes[category] || ''}
                      onChange={(e) => setSectionNotes({ ...sectionNotes, [category]: e.target.value })}
                      placeholder="Enter notes or comments for this section..."
                      disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
              );
            });

            return (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {groupedSections}
                </Box>
                <Box
                  sx={{
                    display: { xs: rightPanelCollapsed ? 'none' : 'block', lg: 'block' },
                    width: { xs: '100%', lg: rightPanelCollapsed ? 52 : 280 },
                    flexShrink: 0,
                    position: { xs: 'static', lg: 'sticky' },
                    top: { lg: 12 },
                    alignSelf: 'flex-start',
                    maxHeight: { lg: 'calc(100vh - 80px)' },
                    overflowY: { lg: 'auto' },
                    pr: 0,
                    p: rightPanelCollapsed ? 0.75 : 0,
                    border: rightPanelCollapsed ? '1px solid #bbdefb' : 'none',
                    borderRadius: rightPanelCollapsed ? 1.5 : 0,
                    bgcolor: rightPanelCollapsed ? '#f8fbff' : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: rightPanelCollapsed ? 'center' : 'flex-end', alignItems: 'center', mb: 1 }}>
                    <Tooltip title={rightPanelCollapsed ? 'Show performance panel' : 'Shrink performance panel'}>
                      <IconButton
                        size="small"
                        onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
                        sx={{
                          border: '1px solid #90caf9',
                          bgcolor: '#e3f2fd',
                          color: '#0f4c81',
                          '&:hover': { bgcolor: '#bbdefb' },
                        }}
                      >
                        {rightPanelCollapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {rightPanelCollapsed ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      {(assessmentType === 'master-training' ? ['Rating Reference', 'Performance Feedback'] : ['Performance Feedback']).map((label) => (
                        <Typography
                          key={label}
                          variant="caption"
                          sx={{
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                            fontWeight: 900,
                            color: '#0f4c81',
                            letterSpacing: 0.3,
                            lineHeight: 1,
                          }}
                        >
                          {label}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                  <>
                  {assessmentType === 'master-training' && (
                    <Accordion defaultExpanded sx={{ mb: 1.5, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Rating Reference
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label="1" size="small" sx={{ bgcolor: '#f44336', color: 'white', fontWeight: 700, minWidth: 28 }} />
                          <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            Inconsistent contact with shuttle and ready stance
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label="2" size="small" sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 700, minWidth: 28 }} />
                          <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            1-3 shots with proper grip/action but inefficient footwork
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label="3" size="small" sx={{ bgcolor: '#ffc107', color: 'white', fontWeight: 700, minWidth: 28 }} />
                          <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            4-10 shot rally with control and basic footwork
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label="4" size="small" sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 700, minWidth: 28 }} />
                          <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            10+ rallies consistently with pace and accuracy
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label="5" size="small" sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 700, minWidth: 28 }} />
                          <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            15+ rallies with rapid footwork, high speed and tactics
                          </Typography>
                        </Box>
                      </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                  <Accordion defaultExpanded sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Performance Feedback
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                    {feedbackSummary ? (
                      <>
                        <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, mb: 0.5 }}>
                          {feedbackSummary.daysSincePreviousSave === null
                            ? 'First saved baseline'
                            : `Compared with previous save: ${feedbackSummary.daysSincePreviousSave} day(s)`}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                          Current save: {new Date(feedbackSummary.currentSavedAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 700 }}>
                          Increased: {feedbackSummary.increased}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700 }}>
                          No Changes: {feedbackSummary.same}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'error.main', fontWeight: 700, mb: 1 }}>
                          Decreased: {feedbackSummary.decreased}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700 }}>
                          Improvement Rate vs Last Save: {feedbackSummary.improvementRatePct.toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, mb: 1 }}>
                          Total Score: {feedbackSummary.currentTotalScore}/{feedbackSummary.maxTotalScore} ({feedbackSummary.totalScorePct.toFixed(1)}%)
                        </Typography>
                        {totalScoreTrendData.length > 1 && (
                          <Box sx={{ width: '100%', height: 120, mb: 1 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={totalScoreTrendData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" hide />
                                <YAxis domain={[0, 100]} allowDecimals />
                                <RechartsTooltip formatter={(value: any) => `${Number(value).toFixed(1)}%`} />
                                <Line type="monotone" dataKey="scorePct" stroke="#1976d2" strokeWidth={2} dot />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                        {improvedSkillsForPanel.length > 0 ? (
                          <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                              Improved Skills
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, m: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              {improvedSkillsForPanel.map((change) => (
                                <Box component="li" key={change.itemKey} sx={{ listStyleType: 'disc' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <StarIcon sx={{ color: 'success.main', fontSize: 14 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>
                                      {getDisplayItemName(change.itemName)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' }}>
                                    {change.previous} {'->'} {change.current}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No improved skills in the last save.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Save or submit once to view persistent change summary.
                      </Typography>
                    )}
                    </AccordionDetails>
                  </Accordion>
                  </>
                  )}
                </Box>
              </Box>
            );
          }

          return filteredItems.map(item => renderItemRow(item));
      })()}
      </Box>

      {/* Table design retained for future reuse */}
      <TableContainer component={Paper} sx={{ boxShadow: 2, display: 'none' }}>
        <Table sx={{ tableLayout: 'auto', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ 
              bgcolor: '#f5f5f5', 
              borderTop: '2px solid #ddd !important', 
              borderBottom: '2px solid #ddd !important',
            }}>
              {!isSubmitted && selectedEvaluation === 'current' && canEdit && (
                <TableCell padding="checkbox" sx={{ borderTop: '2px solid #ddd !important', borderBottom: '2px solid #ddd !important', width: '48px' }}>
                  <Checkbox
                    indeterminate={isIndeterminate()}
                    checked={isAllSelected()}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit}
                    size="small"
                  />
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 600, borderTop: '2px solid #ddd !important', borderBottom: '2px solid #ddd !important', width: '45%', minWidth: 300 }}>Skill</TableCell>
              <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #ddd !important', width: '12%' }}>Not Applicable</TableCell>
              <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #ddd !important', width: '18%' }}>Rating</TableCell>
              <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #ddd !important', width: '15%' }}>Performance</TableCell>
              <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #ddd !important', width: '10%' }}>Smiley</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // Show loading state while fetching assessment items
              if (loadingItems) {
                return (
                  <TableRow>
                    <TableCell colSpan={!isSubmitted && selectedEvaluation === 'current' && canEdit ? 6 : 5} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading assessment skills...
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              
              // Show empty state if no items for this sport (but only if not loading)
              if (!loadingItems && performanceItems.length === 0 && assessmentItems.length === 0) {
                return (
                    <TableRow>
                      <TableCell colSpan={!isSubmitted && selectedEvaluation === 'current' && canEdit ? 6 : 5} align="center" sx={{ py: 4 }}>
                        <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
                          <Typography variant="h6" gutterBottom>
                            No assessment skills defined for {selectedSport}
                          </Typography>
                          <Typography variant="body2">
                            Assessment skills need to be added to the database for this sport. 
                            Please contact your administrator to add skills for {selectedSport}.
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                            Debug: selectedSport={selectedSport}, assessmentType={assessmentType}, loadingItems={loadingItems ? 'true' : 'false'}, assessmentItemsCount={assessmentItems.length}
                          </Typography>
                        </Alert>
                      </TableCell>
                    </TableRow>
                );
              }
              
              // Show loading state while assessment items are being fetched
              if (loadingItems && assessmentItems.length === 0) {
                return (
                    <TableRow>
                      <TableCell colSpan={!isSubmitted && selectedEvaluation === 'current' && canEdit ? 6 : 5} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          Loading assessment skills...
                        </Typography>
                      </TableCell>
                    </TableRow>
                );
              }
              
              // Show empty state if items loaded but performanceItems not initialized
              if (performanceItems.length === 0 && assessmentItems.length > 0) {
                return (
                    <TableRow>
                      <TableCell colSpan={!isSubmitted && selectedEvaluation === 'current' && canEdit ? 6 : 5} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          Initializing assessment form...
                        </Typography>
                      </TableCell>
                    </TableRow>
                );
              }

              // Group items by category for low-level, fitness, and drill assessments
              if (assessmentType === 'master-training' || assessmentType === 'fitness' || assessmentType === 'drill') {
                // Apply rated/not-rated filter first
                let filteredItems = performanceItems;
                if (filterRatedStatus === 'rated') {
                  filteredItems = performanceItems.filter(item => item.rating > 0 && !item.notApplicable);
                } else if (filterRatedStatus === 'not-rated') {
                  filteredItems = performanceItems.filter(item => item.rating === 0 || item.notApplicable);
                } else if (['1', '2', '3', '4', '5'].includes(filterRatedStatus)) {
                  // Filter by specific rating value
                  const ratingValue = parseInt(filterRatedStatus);
                  filteredItems = performanceItems.filter(item => item.rating === ratingValue && !item.notApplicable);
                }
                
                const groupedItems = filteredItems.reduce((acc, item) => {
                  if (!acc[item.category]) {
                    acc[item.category] = [];
                  }
                  acc[item.category].push(item);
                  return acc;
                }, {} as Record<string, BasicPerformanceItem[]>);

                return Object.entries(groupedItems).map(([category, items]) => {
                  // Calculate average score for this section
                  const ratedItems = items.filter(item => !item.notApplicable && item.rating > 0);
                  const averageScore = ratedItems.length > 0
                    ? (ratedItems.reduce((sum, item) => sum + item.rating, 0) / ratedItems.length).toFixed(2)
                    : 'N/A';
                  
                  return (
                    <React.Fragment key={category}>
                      {/* Category Header Row with ID for navigation */}
                      {/* Note: Text alignment intended for left/beginning, but Material-UI TableCell with colSpan 
                          centers the content by default. Multiple attempts to force left alignment (align prop, 
                          textAlign in sx, Box wrapper) did not override the default centering behavior. 
                          Current implementation shows text centered, which matches PDF report behavior. */}
                      <TableRow 
                        id={`section-${category}`}
                        sx={{ 
                          scrollMarginTop: '100px',
                          '&::before': {
                            content: '""',
                            display: 'block',
                            height: '100px',
                            marginTop: '-100px',
                            visibility: 'hidden',
                          },
                          bgcolor: getCategoryColor(category).bg,
                        }}
                      >
                        <TableCell 
                          colSpan={!isSubmitted && selectedEvaluation === 'current' && canEdit ? 6 : 5}
                          sx={{ 
                            fontWeight: 700, 
                            fontSize: '0.9rem', 
                            py: 1.5,
                            pl: 3,
                            pr: 2,
                            color: getCategoryColor(category).text,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            borderBottom: `2px solid ${getCategoryColor(category).text}`,
                            textAlign: 'left',
                            verticalAlign: 'middle',
                          }}
                        >
                          {category}
                        </TableCell>
                      </TableRow>
                      {/* Items in this category */}
                      {items.map((item, itemIndex) => {
                        const performance = getPerformanceLevel(item.rating);
                        const isFirstItem = itemIndex === 0;
                        
                        return (
                          <TableRow key={item.id} hover>
                            {!isSubmitted && selectedEvaluation === 'current' && canEdit && (
                              <TableCell padding="checkbox" sx={{
                                borderTop: isFirstItem ? `2px solid ${getCategoryColor(category).text} !important` : 'none'
                              }}>
                                <Checkbox
                                  checked={selectedItems.has(item.id)}
                                  onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                                  disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit || item.notApplicable}
                                  size="small"
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ 
                              fontWeight: 500, 
                              pl: 3, 
                              width: '45%', 
                              minWidth: 300, 
                              whiteSpace: 'nowrap',
                              borderTop: isFirstItem ? `2px solid ${getCategoryColor(category).text} !important` : 'none'
                            }}>{getDisplayItemName(item.name)}</TableCell>
                            <TableCell sx={{
                              borderTop: isFirstItem ? `2px solid ${getCategoryColor(category).text} !important` : 'none'
                            }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={item.notApplicable || false}
                                    onChange={(e) => handleNotApplicableChange(item.id, e.target.checked)}
                                    disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit}
                                    size="small"
                                  />
                                }
                                label=""
                              />
                            </TableCell>
                            <TableCell sx={{
                              borderTop: isFirstItem ? `2px solid ${getCategoryColor(category).text} !important` : 'none'
                            }}>
                              <Rating
                                value={item.rating}
                                onChange={(_, newValue) => {
                                  if (newValue !== null) {
                                    handleRatingChange(item.id, newValue);
                                  }
                                }}
                                size="small"
                                disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit || item.notApplicable}
                              />
                            </TableCell>
                            <TableCell sx={{
                              borderTop: isFirstItem ? `2px solid ${getCategoryColor(category).text} !important` : 'none'
                            }}>
                              <Typography 
                                variant="body2" 
                                sx={{ color: performance.color, fontWeight: 500 }}
                              >
                                {item.notApplicable ? 'Not Applicable' : performance.text}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{
                              borderTop: isFirstItem ? `2px solid ${getCategoryColor(category).text} !important` : 'none'
                            }}>
                              {item.notApplicable ? <Typography variant="body2" color="text.secondary">N/A</Typography> : getSmileyIcon(item.rating)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Average Score Row */}
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell colSpan={!isSubmitted && selectedEvaluation === 'current' && isSelfEvaluation ? 6 : 5} sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.9rem',
                          py: 1,
                          borderTop: '1px solid #ddd',
                          borderBottom: '1px solid #ddd'
                        }}>
                          Average Score: {averageScore}
                        </TableCell>
                      </TableRow>
                      {/* Notes/Comments Section */}
                      <TableRow>
                        <TableCell colSpan={!isSubmitted && selectedEvaluation === 'current' && isSelfEvaluation ? 6 : 5} sx={{ py: 2, borderBottom: '2px solid #ddd' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Notes/Comments:
                          </Typography>
                          <TextField
                            multiline
                            rows={5}
                            fullWidth
                            value={sectionNotes[category] || ''}
                            onChange={(e) => setSectionNotes({ ...sectionNotes, [category]: e.target.value })}
                            placeholder="Enter notes or comments for this section..."
                            disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit}
                            variant="outlined"
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontSize: '0.875rem',
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                });
              } else {
                // Original table structure for quick assessment
                // Apply rated/not-rated filter
                let filteredItems = performanceItems;
                if (filterRatedStatus === 'rated') {
                  filteredItems = performanceItems.filter(item => item.rating > 0 && !item.notApplicable);
                } else if (filterRatedStatus === 'not-rated') {
                  filteredItems = performanceItems.filter(item => item.rating === 0 || item.notApplicable);
                } else if (['1', '2', '3', '4', '5'].includes(filterRatedStatus)) {
                  // Filter by specific rating value
                  const ratingValue = parseInt(filterRatedStatus);
                  filteredItems = performanceItems.filter(item => item.rating === ratingValue && !item.notApplicable);
                }
                
                return (
                  <>
                    {filteredItems.map((item) => {
                      const performance = getPerformanceLevel(item.rating);
                      
                      return (
                        <TableRow key={item.id} hover>
                          {!isSubmitted && selectedEvaluation === 'current' && canEdit && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedItems.has(item.id)}
                                onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                                disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit || item.notApplicable}
                                size="small"
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 500, width: '45%', minWidth: 300, whiteSpace: 'nowrap' }}>{getDisplayItemName(item.name)}</TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item.notApplicable || false}
                                  onChange={(e) => handleNotApplicableChange(item.id, e.target.checked)}
                                  disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit}
                                  size="small"
                                />
                              }
                              label=""
                            />
                          </TableCell>
                          <TableCell>
                            <Rating
                              value={item.rating}
                              onChange={(_, newValue) => {
                                if (newValue !== null) {
                                  handleRatingChange(item.id, newValue);
                                }
                              }}
                              size="small"
                              disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit || item.notApplicable}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ color: performance.color, fontWeight: 500 }}
                            >
                              {item.notApplicable ? 'Not Applicable' : performance.text}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.notApplicable ? <Typography variant="body2" color="text.secondary">N/A</Typography> : getSmileyIcon(item.rating)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                );
              }
            })()}
          </TableBody>
        </Table>
      </TableContainer>


      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Overall Notes & Feedback"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any overall observations, feedback, or recommendations..."
          disabled={isSubmitted || selectedEvaluation !== 'current' || !canEdit}
        />
      </Box>

      {isSubmitted && selectedEvaluation === 'current' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          ✅ Evaluation submitted for {getPeriodLabel(evaluationPeriod)} {evaluationYear} period. Performance smiles are now visible!
        </Alert>
      )}

      {selectedEvaluation !== 'current' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          📊 Viewing previous evaluation from {previousEvaluations.find(evaluation => evaluation.id === selectedEvaluation)?.submittedAt}
        </Alert>
      )}
    </Box>
  );
};

export default BasicPerformanceTracking;